<template>
    <div class="hotel-partner-form mb-n5">
        <!-- Steps + help -->
        <div class="row" v-if="! currentEdit && ! isSuccess">
            <!-- Steps -->
            <div class="steps" :class="{ 'col-lg-6': currentStep !== 'summary', 'col-lg-12': currentStep === 'summary' }">
                <!-- Steps bar -->
                <div class="steps-bar d-md-none">
                    <ul class="steps-bar-indicator">
                        <li v-for="(step, index) in steps" :class="{ current: steps.indexOf(currentStep) === index, done: steps.indexOf(currentStep) > index, default: steps.indexOf(step) > steps.indexOf(currentStep) }">
                        </li>
                    </ul>
                </div>

                <!-- Product types -->
                <div class="step--product-types fadeIn wow" v-show="currentStep === 'productTypes'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        {{ 'partners.hotels.form.steps.productTypes.heading' | trans }}
                    </h2>

                    <p class="lead">
                        {{ 'partners.hotels.form.steps.productTypes.info' | trans }}
                    </p>

                    <p class="text-primary">
                        <i class="fa fa-info-circle mr-1"></i>
                        {{ 'partners.hotels.form.steps.productTypes.multiselectionPossible' | trans }}
                    </p>

                    <!-- List select -->
                    <ul class="list-select">
                        <li v-for="item in possibleProductTypes" :class="{ selected: productTypes.indexOf(item.type) !== -1 }" @click="toggleProductType(item.type)">
                            <!-- Badge -->
                            <span class="badge" :class="'badge-' + item.badgeColor" v-if="item.badge">
                                {{ item.badge }}
                            </span>

                            <!-- Icon -->
                            <i :class="item.icon"></i>

                            <div>
                                <!-- Title -->
                                <span class="title">
                                    {{ item.title }}
                                </span>

                                <!-- Subtitle -->
                                <small class="subtitle">
                                    {{ item.subtitle }}
                                </small>

                                <!-- Info -->
                                <small class="info" v-if="item.info">
                                    <i :class="item.infoIcon" v-if="item.infoIcon"></i>
                                    {{ item.info }}
                                </small>
                            </div>
                        </li>
                    </ul>

                    <div class="d-flex">
                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="next" :disabled="productTypes.length === 0">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Details -->
                <div class="step--details fadeIn wow" v-show="currentStep === 'details'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        <small class="top-promo">
                            {{ 'partners.hotels.form.steps.details.topPromo' | trans }}
                        </small>

                        {{ 'partners.hotels.form.steps.details.heading' | trans }}
                    </h2>

                    <p class="lead">
                        {{ 'partners.hotels.form.steps.details.info' | trans }}
                    </p>

                    <!-- Hotel details -->
                    <div class="card border mb-3" v-if="hotel">
                        <div class="card-body">
                            <button type="button" class="btn btn-link btn-sm float-right" @click="editHotel">
                                <i class="fa fa-pencil"></i>
                                {{ 'partners.hotels.form.edit' | trans }}
                            </button>

                            <h3 class="card-title">
                                {{ hotel.name }}
                            </h3>
                            <p class="card-text">
                                {{ hotel.subname }}
                            </p>
                        </div>
                    </div>

                    <div v-if="! hotel">
                        <!-- Hotel search -->
                        <div v-if="! isManualHotel">
                            <span class="hotel-search-label">
                                {{ 'partners.hotels.form.steps.details.searchHotel' | trans }}
                            </span>

                            <div class="hotel-query-input">
                                <svg class="loader my-3" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-if="isLoadingHotels">
                                    <circle class="internal-circle" cx="60" cy="60" r="30"></circle>
                                    <circle class="external-circle" cx="60" cy="60" r="50"></circle>
                                </svg>

                                <input type="text" class="form-control form-control-lg" v-model="hotelQuery" placeholder="z.B. Gartenhotel am See" />
                            </div>

                            <!-- Error -->
                            <div class="alert alert-danger my-3" v-if="isLoadingHotelsError">
                                {{ 'partners.hotels.form.steps.details.hotelsError' | trans }}
                            </div>

                            <!-- Hotel results -->
                            <div class="list-group list-group-flush my-3 hotel-results" v-if="Array.isArray(hotels) && hotels.length > 0">
                                <button type="button" class="list-group-item list-group-item-action" v-for="hotel in hotels.slice(0, 5)" @click="selectHotel(hotel)">
                                    <span class="name">
                                        {{  hotel.name }}
                                    </span>

                                    <small class="d-block">
                                        {{  hotel.subname }}
                                    </small>
                                </button>
                            </div>

                            <!-- Powered by Google (Logo) -->
                            <img class="d-block" src="data:image/png;base64,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" style="height:18px;" v-if="hotels.length > 0" />

                            <!-- Toggle manual -->
                            <button type="button" class="btn btn-link px-3 ml-n3 mt-2" @click="toggleDetails">
                                {{ 'partners.hotels.form.steps.details.enterManual' | trans }}
                            </button>
                        </div>

                        <!-- Manuel hotel -->
                        <div v-if="isManualHotel">
                            <span class="hotel-search-label">
                                {{ 'partners.hotels.form.steps.details.enterHotel' | trans }}
                            </span>

                            <p class="form-text text-muted" v-html="$options.filters.trans('partners.hotels.form.steps.details.manualHotelInfo')"></p>

                            <!-- Name -->
                            <div class="form-group">
                                <label for="hotel_name">
                                    {{ 'partners.hotels.form.steps.details.hotelName' | trans }}
                                </label>

                                <input type="text" class="form-control" id="hotel_name" v-model="manualHotelName" placeholder="z.B. Gartenhotel am See" />
                            </div>

                            <!-- Address -->
                            <!-- Street house number -->
                            <div class="form-group">
                                <label for="hotel_streetHouseNumber">
                                    {{ 'partners.hotels.form.steps.details.hotelStreetHouseNumber' | trans }}
                                </label>

                                <input type="text" class="form-control" id="hotel_streetHouseNumber" v-model="manualHotelStreetHouseNumber" placeholder="z.B. Musterstraße 1a" />
                            </div>

                            <!-- Zipcode + City -->
                            <div class="row">
                                <div class="col-5">
                                    <div class="form-group">
                                        <label for="hotel_zipcode">
                                            {{ 'partners.hotels.form.steps.details.hotelZipcode' | trans }}
                                        </label>

                                        <input type="text" class="form-control" id="hotel_zipcode" v-model="manualHotelZipcode" placeholder="z.B. 55443" />
                                    </div>
                                </div>
                                <div class="col-7">
                                    <div class="form-group">
                                        <label for="hotel_city">
                                            {{ 'partners.hotels.form.steps.details.hotelCity' | trans }}
                                        </label>

                                        <input type="text" class="form-control" id="hotel_city" v-model="manualHotelCity" placeholder="z.B. Musterstadt" />
                                    </div>
                                </div>
                            </div>

                            <!-- Country -->
                            <div class="form-group">
                                <label for="hotel_country">
                                    {{ 'partners.hotels.form.steps.details.hotelCountry' | trans }}
                                </label>

                                <div class="form-group">
                                    <div class="input-group custom-input-group">
                                        <div class="input-group-prepend">
                                            <label class="input-group-text" for="country">
                                                <!-- Flag icon -->
                                                <i class="flag-icon" :class="'flag-icon-' + manualHotelCountry"></i>
                                            </label>
                                        </div>

                                        <select v-model="manualHotelCountry" id="country" class="custom-select">
                                            <option :value="country" v-for="country in hotelSelectableCountries">
                                                {{ 'checkout.steps.confirmation.countries.' + country | trans }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <!-- Invalid info -->
                            <p class="text-muted" v-if="! validManualHotel">
                                <i class="fa fa-info-circle mr-2"></i>
                                {{ 'partners.hotels.form.steps.details.invalidManualHotel' | trans }}
                            </p>

                            <!-- Done -->
                            <button class="btn btn-block btn-primary btn-lg" @click="useHotel" :disabled="! validManualHotel">
                                {{ 'partners.hotels.form.steps.details.saveManualHotel' | trans }}
                            </button>

                            <!-- Toggle manual -->
                            <button type="button" class="btn btn-link btn-block mt-2" @click="toggleDetails">
                                <i class="fa fa-search mr-1"></i>
                                {{ 'partners.hotels.form.steps.details.searchHotel' | trans }}
                            </button>

                            <div class="divider"></div>
                        </div>
                    </div>

                    <div class="d-flex">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" :disabled="! hotel" @click="next">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Connections -->
                <div class="step--connections fadeIn wow" v-show="currentStep === 'connections'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        {{ 'partners.hotels.form.steps.connections.heading' | trans }}
                    </h2>

                    <p class="lead">
                        {{ 'partners.hotels.form.steps.connections.info' | trans }}
                    </p>

                    <p class="text-primary">
                        <i class="fa fa-info-circle mr-1"></i>
                        {{ 'partners.hotels.form.steps.connections.multiselectionPossible' | trans }}
                    </p>

                    <div v-if="! isShowAddCustomChannelManager">

                        <!-- Search query -->
                        <span class="hotel-search-label">
                            {{ 'partners.hotels.form.steps.connections.searchChannelManagers' | trans }}
                        </span>

                        <div class="hotel-query-input">
                            <input type="text" class="form-control form-control-lg" v-model="channelManagerQuery" placeholder="z.B. SiteMinder" />
                        </div>

                        <!-- List group -->
                        <div class="list-group list-group-flush channel-manager-results mt-3">
                            <!-- Custom -->
                            <button type="button" class="list-group-item list-group-item-action text-primary" @click="isShowAddCustomChannelManager = ! isShowAddCustomChannelManager">
                                <i class="fa fa-plus mr-2 text-primary"></i>
                                <span class="name text-primary">
                                    {{ 'partners.hotels.form.steps.connections.addCustom' | trans }}
                                </span>
                            </button>

                            <button type="button" class="list-group-item list-group-item-action" v-for="item in filteredChannelManagers.slice(0, maxChannelManagers)" :class="{ active: $lodash.findIndex(channelManagers, { id: item.id }) !== -1 }" @click="toggleChannelManager(item)">

                                <i class="far fa-circle mr-2" v-if="$lodash.findIndex(channelManagers, { name: item.name }) === -1"></i>
                                <i class="far fa-check-circle mr-2 text-primary" v-if="$lodash.findIndex(channelManagers, { name: item.name }) !== -1"></i>

                                <!-- Name -->
                                <span class="name">
                                    {{ item.name }}
                                </span>
                            </button>
                        </div>

                        <!-- Show more -->
                        <button type="button" class="btn btn-outline-primary btn-block btn-lg mt-1" @click="maxChannelManagers += 5" v-if="filteredChannelManagers.length > maxChannelManagers">
                            {{ 'partners.hotels.form.showMore' | trans }}
                        </button>

                        <!-- No results -->
                        <p class="text-center my-3 text-muted" v-if="possibleChannelManagers.length > 0 && filteredChannelManagers.length === 0">
                            {{ 'partners.hotels.form.steps.connections.noResults' | trans }}
                        </p>

                        <div class="d-flex mt-3">
                            <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                                {{ 'partners.hotels.form.back' | trans }}
                            </button>

                            <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="next">
                                <span v-if="channelManagers.length > 0">
                                    {{ 'partners.hotels.form.next' | trans }}
                                </span>
                                <span v-if="channelManagers.length === 0">
                                    {{ 'partners.hotels.form.skip' | trans }}
                                </span>
                                <i class="wa-icon-long-arrow-right"></i>
                            </button>
                        </div>
                    </div>

                    <div v-if="isShowAddCustomChannelManager">
                        <!-- Search query -->
                        <span class="hotel-search-label">
                            {{ 'partners.hotels.form.steps.connections.customChannelManager' | trans }}
                        </span>

                        <div class="hotel-query-input">
                            <input type="text" class="form-control form-control-lg" v-model="customChannelManagerName" placeholder="z.B. SiteMinder" />
                        </div>

                        <!-- Add button -->
                        <div class="d-flex mt-3">
                            <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                                {{ 'partners.hotels.form.back' | trans }}
                            </button>

                            <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="addCustomChannelManager">
                                <span v-if="customChannelManagerName.length > 0">
                                    {{ 'partners.hotels.form.next' | trans }}
                                </span>
                                <span v-if="customChannelManagerName.length === 0">
                                    {{ 'partners.hotels.form.skip' | trans }}
                                </span>
                                <i class="wa-icon-long-arrow-right"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Components -->
                <div class="step--components fadeIn wow" v-show="currentStep === 'components'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        {{ 'partners.hotels.form.steps.components.heading' | trans }}
                    </h2>

                    <p class="lead mb-4">
                        {{ 'partners.hotels.form.steps.components.info' | trans }}
                    </p>

                    <!-- Rooms -->
                    <div class="rooms">
                        <!-- Add room dropdown -->
                        <div class="dropdown float-right" :class="{ open: showDropdown === 'addRoom' }">
                            <button class="btn btn-link btn-sm mt-n2 dropdown-toggle not pb-1" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="toggleDropdown('addRoom');">
                                <i class="fa fa-plus mr-1"></i>
                                {{ 'partners.hotels.form.add' | trans }}
                            </button>

                            <div class="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === 'addRoom' }">
                                <!-- Individual -->
                                <a class="dropdown-item d-flex mx-0 px-2" href="#" @click.prevent="addRoom(null)">
                                    <span class="col font-weight-500 text-primary">
                                        {{ 'partners.hotels.form.steps.components.addIndividualRoom' | trans }}
                                    </span>
                                </a>

                                <div class="dropdown-divider"></div>

                                <!-- Templates -->
                                <h6 class="dropdown-header px-4">
                                    {{ 'partners.hotels.form.steps.components.templates' | trans }}
                                </h6>
                                <a v-for="roomType in possibleRoomTypes" class="dropdown-item mx-0 px-4" href="#" @click.prevent="addRoom(roomType)">
                                    <!-- Name -->
                                    <span class="font-weight-500">
                                        {{ roomType.name }}
                                    </span>

                                    <!-- Occupancy -->
                                    <small class="d-block text-muted">
                                        <!-- Adults -->
                                        <span v-if="roomType.min_adults !== roomType.max_adults">
                                            {{ roomType.min_adults }}
                                            bis
                                            {{ roomType.max_adults }}
                                            <i class="fa fa-user"></i>
                                        </span>
                                        <span v-if="roomType.min_adults === roomType.max_adults">
                                            {{ roomType.min_adults }}
                                            <i class="fa fa-user"></i>
                                        </span>

                                        <span class="px-1">|</span>

                                        <!-- Children -->
                                        <span v-if="roomType.min_children !== roomType.max_children">
                                            {{ roomType.min_children }}
                                            bis
                                            {{ roomType.max_children }}
                                            <i class="fa fa-child"></i>
                                        </span>
                                        <span v-if="roomType.min_children === roomType.max_children">
                                            {{ roomType.min_children }}
                                            <i class="fa fa-child"></i>
                                        </span>

                                        <span class="px-1">|</span>

                                        <!-- Persons -->
                                        <span v-if="roomType.min_persons !== roomType.max_persons">
                                            {{ roomType.min_persons }}
                                            bis
                                            {{ roomType.max_persons }}
                                            <i class="fa fa-users"></i>
                                        </span>
                                        <span v-if="roomType.min_persons === roomType.max_persons">
                                            {{ roomType.min_persons }}
                                            <i class="fa fa-users"></i>
                                        </span>
                                    </small>
                                </a>
                            </div>
                        </div>

                        <h6 class="mb-3">
                            {{ 'partners.hotels.form.steps.components.rooms' | trans }}
                        </h6>

                        <div class="alert alert-warning my-3" v-if="rooms.length === 0">
                            <i class="fa fa-info-circle mr-1"></i>
                            {{ 'partners.hotels.form.steps.components.roomsInfo' | trans }}
                        </div>

                        <!-- Single room -->
                        <div class="card border mb-2" v-for="room in rooms">
                            <div class="card-body d-flex align-items-center">
                                <i class="fa fa-bed font-weight-100 mr-3" style="font-size: 24px;"></i>

                                <div class="flex-grow-1">
                                    <!-- Name -->
                                    <strong class="d-block">
                                        {{  room.name }}
                                    </strong>

                                    <!-- Details -->
                                    <small class="d-block text-muted">
                                        <!-- Occupancy -->
                                        {{ room.occupancy.maxPersons }}
                                        {{ 'partners.hotels.form.steps.components.persons' | trans }}

                                        <!-- Min + max price -->
                                        <span v-if="room.availabilities.length > 0">
                                            &bull;
                                            {{ $lodash.minBy(room.availabilities, 'price') | formatPrice }}
                                            {{ 'partners.hotels.form.steps.components.priceTo' | trans }}
                                            {{ $lodash.maxBy(room.availabilities, 'price') | formatPrice }}
                                            &euro;
                                        </span>

                                        <!-- Images -->
                                        <span v-if="room.images.length > 0">
                                            &bull;
                                            {{ room.images.length }}
                                            <i class="fa fa-images"></i>
                                        </span>
                                    </small>

                                    <!-- Validation -->
                                    <span class="badge badge-danger" v-if="! (validRoomInfo(room) && validBoardType(room) && validRoomOccupancy(room) && validRoomAvailabilities(room) && validRoomCancellationRules(room))">
                                        {{ 'partners.hotels.form.steps.components.incomplete' | trans }}
                                    </span>
                                    <span class="badge badge-success" v-if="validRoomInfo(room) && validBoardType(room) && validRoomOccupancy(room) && validRoomAvailabilities(room) && validRoomCancellationRules(room)">
                                        {{ 'partners.hotels.form.steps.components.complete' | trans }}
                                    </span>
                                </div>

                                <button type="button" class="btn btn-link" title="Löschen" @click="removeRoom(room)">
                                    <i class="fa fa-trash"></i>
                                </button>

                                <button type="button" class="btn btn-link" title="Bearbeiten" @click="editRoom(room)">
                                    <i class="fa fa-pencil"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="divider"></div>

                    <!-- Board types -->
                    <div class="board-types">
                        <!-- Add room dropdown -->
                        <div class="dropdown float-right" :class="{ open: showDropdown === 'addBoardType' }">
                            <button class="btn btn-link btn-sm mt-n2 dropdown-toggle not pb-1" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="toggleDropdown('addBoardType');">
                                <i class="fa fa-plus mr-1"></i>
                                {{ 'partners.hotels.form.add' | trans }}
                            </button>

                            <div class="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === 'addBoardType' }">
                                <!-- Individual -->
                                <a class="dropdown-item d-flex mx-0 px-2" href="#" @click.prevent="addBoardType(null)">
                                    <span class="col font-weight-500 text-primary">
                                        {{ 'partners.hotels.form.steps.components.addIndividualBoardType' | trans }}
                                    </span>
                                </a>

                                <div class="dropdown-divider"></div>

                                <!-- Templates -->
                                <h6 class="dropdown-header px-4">
                                    {{ 'partners.hotels.form.steps.components.templates' | trans }}
                                </h6>
                                <a v-for="boardType in possibleBoardTypes" class="dropdown-item d-flex mx-0 px-2" href="#" @click.prevent="addBoardType(boardType)">
                                    <!-- Name -->
                                    <span class="col font-weight-500">
                                        {{ boardType.name }}
                                    </span>

                                    <!-- Code -->
                                    <small class="col text-muted text-right">
                                        {{ boardType.code }}
                                    </small>
                                </a>
                            </div>
                        </div>

                        <h6 class="mb-3">
                            {{ 'partners.hotels.form.steps.components.additionalBoardType' | trans }}
                        </h6>

                        <p class="text-muted" v-if="boardTypes.length === 0">
                            <i class="fa fa-info-circle mr-1"></i>
                            {{ 'partners.hotels.form.steps.components.additionalBoardTypeInfo' | trans }}
                        </p>

                        <!-- Single board type -->
                        <div class="card border mb-2" v-for="boardType in boardTypes">
                            <div class="card-body d-flex align-items-center">
                                <i class="fa fa-utensils font-weight-100 mr-3" style="font-size: 24px;"></i>

                                <div class="flex-grow-1">
                                    <!-- Name -->
                                    <strong class="d-block">
                                        {{  boardType.name }}

                                        <small class="text-muted ml-1">
                                            {{  boardType.code }}
                                        </small>
                                    </strong>

                                    <!-- Details -->
                                    <small class="d-block text-muted">
                                        <!-- Extra charge -->
                                        <span v-if="boardType.extraCharge > 0">
                                            {{ boardType.extraCharge | formatPrice }}
                                            &euro;
                                        </span>

                                        <!-- Weekdays -->
                                        <span v-if="! (boardType.isMonday && boardType.isTuesday && boardType.isWednesday && boardType.isThursday && boardType.isFriday && boardType.isSaturday && boardType.isSunday)">
                                            &bull;
                                            {{ weekdaysList(boardType) }}
                                        </span>
                                    </small>

                                    <!-- Description -->
                                    <small class="d-block text-muted" v-if="boardType.description">
                                        {{  boardType.description }}
                                    </small>

                                    <!-- Validation -->
                                    <span class="badge badge-danger" v-if="! (validBoardTypeInfo(boardType) && validBoardTypeAvailability(boardType) && validBoardTypeExtraCharge(boardType))">
                                        {{ 'partners.hotels.form.steps.components.incomplete' | trans }}
                                    </span>
                                    <span class="badge badge-success" v-if="validBoardTypeInfo(boardType) && validBoardTypeAvailability(boardType) && validBoardTypeExtraCharge(boardType)">
                                        {{ 'partners.hotels.form.steps.components.complete' | trans }}
                                    </span>
                                </div>

                                <button type="button" class="btn btn-link" title="Löschen" @click="removeBoardType(boardType)">
                                    <i class="fa fa-trash"></i>
                                </button>

                                <button type="button" class="btn btn-link" title="Bearbeiten" @click="editBoardType(boardType)">
                                    <i class="fa fa-pencil"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="divider"></div>

                    <!-- Additional services -->
                    <div class="board-types">
                        <button type="button" class="btn btn-link float-right btn-sm mt-n2 pb-1" @click="addAdditionalService">
                            <i class="fa fa-plus mr-1"></i>
                            {{ 'partners.hotels.form.add' | trans }}
                        </button>

                        <h6 class="mb-3">
                            {{ 'partners.hotels.form.steps.components.extras' | trans }}
                        </h6>

                        <p class="text-muted" v-if="additionalServices.length === 0">
                            <i class="fa fa-info-circle mr-1"></i>
                            {{ 'partners.hotels.form.steps.components.extrasInfo' | trans }}
                        </p>

                        <!-- Single additional service -->
                        <div class="card border mb-2" v-for="additionalService in additionalServices">
                            <div class="card-body d-flex align-items-center">
                                <!-- Image -->
                                <img class="rounded mr-3" style="width: 70px; max-height: 70px;" :src="additionalService.image.url" v-if="additionalService.image" />

                                <!-- Icon -->
                                <i class="fa fa-cube font-weight-100 mr-3" style="font-size: 24px;" v-if="! additionalService.image"></i>

                                <div class="flex-grow-1">
                                    <!-- Name -->
                                    <strong class="d-block">
                                        {{  additionalService.name }}
                                    </strong>
                                    <small class="d-block">
                                        {{ additionalService.subtitle }}
                                    </small>

                                    <!-- Details -->
                                    <small class="d-block text-muted">
                                        <!-- Price -->
                                        {{ additionalService.price | formatPrice }}
                                        &euro;

                                        <!-- Amount type -->
                                        <span v-if="additionalService.amountType === 'person'">
                                            {{ 'partners.hotels.form.steps.components.perPerson' | trans }}
                                        </span>
                                        <span v-if="additionalService.amountType === 'booking'">
                                            {{ 'partners.hotels.form.steps.components.perBooking' | trans }}
                                        </span>
                                    </small>

                                    <!-- Validation -->
                                    <span class="badge badge-danger" v-if="! (validAdditionalServiceInfo(additionalService) && validAdditionalServiceDescription(additionalService) && validAdditionalServicePrice(additionalService))">
                                        {{ 'partners.hotels.form.steps.components.incomplete' | trans }}
                                    </span>
                                    <span class="badge badge-success" v-if="validAdditionalServiceInfo(additionalService) && validAdditionalServiceDescription(additionalService) && validAdditionalServicePrice(additionalService)">
                                        {{ 'partners.hotels.form.steps.components.complete' | trans }}
                                    </span>
                                </div>

                                <button type="button" class="btn btn-link" title="Löschen" @click="removeAdditionalService(additionalService)">
                                    <i class="fa fa-trash"></i>
                                </button>

                                <button type="button" class="btn btn-link" title="Bearbeiten" @click="editAdditionalService(additionalService)">
                                    <i class="fa fa-pencil"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex mt-3">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" :disabled="! validComponents" @click="next">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Notification -->
                <div class="step--notifications fadeIn wow" v-show="currentStep === 'notification'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        {{ 'partners.hotels.form.steps.notification.heading' | trans }}
                    </h2>

                    <p class="lead">
                        {{ 'partners.hotels.form.steps.notification.info' | trans }}
                    </p>

                    <p class="text-primary">
                        <i class="fa fa-info-circle mr-1"></i>
                        {{ 'partners.hotels.form.steps.productTypes.multiselectionPossible' | trans }}
                    </p>

                    <div class="alert alert-danger my-3" v-if="notification.types.indexOf('single') === -1">
                        <i class="fa fa-info-circle mr-1"></i>
                        Die Option <strong>E-Mail</strong> muss immer ausgewählt sein.
                    </div>

                    <!-- List select -->
                    <ul class="list-select">
                        <li v-for="item in possibleNotificationTypes" :class="{ selected: notification.types.indexOf(item.type) !== -1 }" @click="toggleNotificationType(item.type)">
                            <!-- Badge -->
                            <span class="badge" :class="'badge-' + item.badgeColor" v-if="item.badge">
                                {{ item.badge }}
                            </span>

                            <!-- Icon -->
                            <i :class="item.icon"></i>

                            <div>
                                <!-- Title -->
                                <span class="title">
                                    {{ item.title }}
                                </span>

                                <!-- Subtitle -->
                                <small class="subtitle">
                                    {{ item.subtitle }}
                                </small>

                                <!-- Info -->
                                <small class="info" v-if="item.info">
                                    <i :class="item.infoIcon" v-if="item.infoIcon"></i>
                                    {{ item.info }}
                                </small>
                            </div>
                        </li>
                    </ul>

                    <p>
                        <small class="text-muted">
                            {{ 'partners.hotels.form.steps.notification.subInfo' | trans }}
                        </small>
                    </p>

                    <div class="divider"></div>

                    <!-- Email -->
                    <div class="form-group">
                        <label for="notificationEmail">
                            {{ 'partners.hotels.form.steps.notification.emailAddress' | trans }}
                            <span class="required">*</span>
                        </label>

                        <input type="email" class="form-control" id="notificationEmail" v-model="notification.email" placeholder="z.B. reservations@beispiel.de" />
                    </div>

                    <div class="d-flex">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="next" :disabled="! validNotification">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Billing -->
                <div class="step--billing fadeIn wow" v-show="currentStep === 'billing'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        {{ 'partners.hotels.form.steps.billing.heading' | trans }}
                    </h2>

                    <p class="lead">
                        {{ 'partners.hotels.form.steps.billing.info' | trans }}
                    </p>

                    <!-- List select -->
                    <ul class="list-select">
                        <li v-for="item in possibleBillingTypes" :class="{ selected: billing.type === item.type }" @click="billing.type = item.type">
                            <!-- Badge -->
                            <span class="badge" :class="'badge-' + item.badgeColor" v-if="item.badge">
                                {{ item.badge }}
                            </span>

                            <!-- Icon -->
                            <i :class="item.icon"></i>

                            <div>
                                <!-- Title -->
                                <span class="title">
                                    {{ item.title }}
                                </span>

                                <!-- Subtitle -->
                                <small class="subtitle">
                                    {{ item.subtitle }}
                                </small>

                                <!-- Info -->
                                <small class="info" v-if="item.info">
                                    <i :class="item.infoIcon" v-if="item.infoIcon"></i>
                                    {{ item.info }}
                                </small>
                            </div>
                        </li>
                    </ul>

                    <!-- Is same email -->
                    <div class="form-check form-check-border" v-if="billing.type === 'payout'">
                        <input type="checkbox" class="form-check-input" id="billingSameAddress" v-model="billing.isSameEmail">
                        <label class="form-check-label" for="billingSameAddress">
                            {{ 'partners.hotels.form.steps.billing.sendPayoutTo' | trans }}
                            <strong>{{ notification.email }}</strong>.
                        </label>
                    </div>

                    <!-- Invoice address -->
                    <p class="mt-2" v-if="billing.type === 'invoice'">
                        <i class="fa fa-info-circle mr-1"></i>
                        {{ 'partners.hotels.form.steps.billing.pleaseSendInvoicesTo' | trans }}
                        <strong class="text-primary">rechnungen@watado.de</strong>.
                    </p>

                    <!-- Email -->
                    <div v-if="! billing.isSameEmail">
                        <div class="divider"></div>

                        <div class="form-group">
                            <label for="billingEmail">
                                {{ 'partners.hotels.form.steps.billing.emailAddressForPayouts' | trans }}
                                <span class="required">*</span>
                            </label>

                            <input type="email" class="form-control" id="billingEmail" v-model="billing.email" placeholder="z.B. buchhaltung@beispiel.de" />
                        </div>
                    </div>

                    <!-- Payout bank account -->
                    <div v-if="billing.type === 'payout'">
                        <div class="divider"></div>

                        <div class="form-group">
                            <label for="billingIban">
                                {{ 'partners.hotels.form.steps.billing.payoutBankAccount' | trans }}
                                <span class="required">*</span>
                            </label>

                            <p class="form-text">
                                {{ 'partners.hotels.form.steps.billing.payoutBankAccountInfo' | trans }}
                            </p>

                            <input type="text" class="form-control" id="billingIban" v-model="billing.iban" placeholder="z.B. DE41500105170123456789" />
                        </div>
                    </div>

                    <div class="d-flex mt-3">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="next" :disabled="! validBilling">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Flash sale -->
                <div class="step--flash-sale fadeIn wow" v-show="currentStep === 'flashSale'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        {{ 'partners.hotels.form.steps.flashSale.heading' | trans }}
                    </h2>

                    <p class="lead">
                        {{ 'partners.hotels.form.steps.flashSale.infoLine1' | trans }}<br />
                        {{ 'partners.hotels.form.steps.flashSale.infoLine2' | trans }}
                    </p>

                    <!-- Possible flash sale types -->
                    <ul class="list-select">
                        <li v-for="item in possibleFlashSaleTypes" :class="{ selected: flashSale.type === item.type }" @click="setFlashSaleType(item.type)">
                            <!-- Badge -->
                            <span class="badge" :class="'badge-' + item.badgeColor" v-if="item.badge">
                                {{ item.badge }}
                            </span>

                            <!-- Icon -->
                            <i :class="item.icon"></i>

                            <div>
                                <!-- Title -->
                                <span class="title">
                                    {{ item.title }}
                                </span>

                                <!-- Subtitle -->
                                <small class="subtitle">
                                    {{ item.subtitle }}
                                </small>

                                <!-- Info -->
                                <small class="info" v-if="item.info">
                                    <i :class="item.infoIcon" v-if="item.infoIcon"></i>
                                    {{ item.info }}
                                </small>
                            </div>
                        </li>
                    </ul>

                    <!-- Period -->
                    <div v-if="flashSale.type === 'period'">
                        <div class="divider"></div>

                        <div class="form-group">
                            <label for="billingEmail">
                                {{ 'partners.hotels.form.steps.flashSale.selectPeriod' | trans }}
                                <span class="required">*</span>
                            </label>

                            <div class="d-flex my-2 align-items-center" v-if="flashSale.from && flashSale.to">
                                <!-- From -->
                                <div class="flex-grow-1">
                                    <div class="bg-light rounded p-3 text-center">
                                        <strong>
                                            {{ flashSale.from.format('Do MMM Y') }}
                                        </strong>
                                    </div>
                                </div>

                                <div class="px-3 text-center">
                                    {{ 'partners.hotels.form.steps.flashSale.periodTo' | trans }}
                                </div>

                                <!-- To -->
                                <div class="flex-grow-1">
                                    <div class="bg-light rounded p-3 text-center">
                                        <strong>
                                            {{ flashSale.to.format('Do MMM Y') }}
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            <div class="dropdown" :class="{ open: showDropdown === 'flashSalePeriod' }">
                                <button class="btn dropdown-toggle" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="toggleDropdown('flashSalePeriod');">
                                    <!-- Placeholder -->
                                    <span v-if="flashSale.from === null && flashSale.to === null">
                                        Zeitraum wählen...
                                    </span>

                                    <!-- Change -->
                                    <span v-if="flashSale.from !== null && flashSale.to !== null">
                                        <i class="fa fa-pencil mr-1"></i>
                                        {{ 'partners.hotels.form.edit' | trans }}
                                    </span>
                                </button>

                                <div class="dropdown-menu period-picker px-3" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === 'flashSalePeriod' }">
                                    <input type="hidden" id="flashSalePeriodPicker" />
                                    <div class="calendar" id="flashSaleCalendar"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="divider"></div>

                    <!-- Possible flash sale targets -->
                    <div class="form-group">
                        <label>
                            Wie sollen wir Ihr Produkt bewerben?
                        </label>
                    </div>
                    <ul class="list-select">
                        <li v-for="item in possibleFlashSaleTargets" :class="{ selected: flashSale.target === item.type }" @click="setFlashSaleTarget(item.type)">
                            <!-- Badge -->
                            <span class="badge" :class="'badge-' + item.badgeColor" v-if="item.badge">
                                {{ item.badge }}
                            </span>

                            <!-- Icon -->
                            <i :class="item.icon"></i>

                            <div>
                                <!-- Title -->
                                <span class="title">
                                    {{ item.title }}
                                </span>

                                <!-- Subtitle -->
                                <small class="subtitle">
                                    {{ item.subtitle }}
                                </small>

                                <!-- Info -->
                                <small class="info" v-if="item.info">
                                    <i :class="item.infoIcon" v-if="item.infoIcon"></i>
                                    {{ item.info }}
                                </small>
                            </div>
                        </li>
                    </ul>

                    <div class="d-flex mt-3">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="next" :disabled="! validFlashSale">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Voucher request -->
                <div class="step--voucher-request fadeIn wow" v-show="currentStep === 'voucherRequest'">
                    <!-- Get help -->
                    <button type="button" class="btn btn-link float-right ml-2 d-md-none" @click="scrollTo('.help')">
                        <i class="fa fa-question-circle"></i>
                        {{ 'partners.hotels.form.help' | trans }}
                    </button>

                    <h2 class="heading">
                        Gutschein-Anfrage
                    </h2>

                    <p class="lead">
                        Wir unterstützen Sie bestmöglich bei Ihrem Gutscheinwunsch.<br />
                        Bitte füllen Sie die unten stehenden Fragen aus.
                    </p>

                    <!-- Redemption type -->
                    <div class="form-group mt-5">
                        <label class="d-block mb-3">
                            Sollen Kunden Sie zur Einlösung anrufen oder möchten Sie die Auslastung per Kontingent steuern?
                        </label>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="redemptionTypeAvailability" value="availability" class="custom-control-input" v-model="voucherRequest.redemptionType">
                            <label class="custom-control-label" for="redemptionTypeAvailability">
                                Kontingent
                            </label>
                        </div>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="redemptionTypeRequest" value="request" class="custom-control-input" v-model="voucherRequest.redemptionType">
                            <label class="custom-control-label" for="redemptionTypeRequest">
                                Kunden sollen uns anrufen
                            </label>
                        </div>
                    </div>

                    <!-- Validity -->
                    <div class="form-group mt-5">
                        <label class="d-block mb-3">
                            Wie lange soll der Gutschein gültig sein?
                        </label>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="validityOneYear" value="oneYear" class="custom-control-input" v-model="voucherRequest.validity">
                            <label class="custom-control-label" for="validityOneYear">
                                1 Jahr
                            </label>
                        </div>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="validityLessThanAYear" value="lessThanAYear" class="custom-control-input" v-model="voucherRequest.validity">
                            <label class="custom-control-label" for="validityLessThanAYear">
                                Weniger als ein Jahr
                            </label>
                        </div>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="validityMoreThanAYear" value="moreThanAYear" class="custom-control-input" v-model="voucherRequest.validity">
                            <label class="custom-control-label" for="validityMoreThanAYear">
                                Mehr als ein Jahr
                            </label>
                        </div>
                    </div>

                    <!-- Existing voucher -->
                    <div class="form-group mt-5">
                        <label class="d-block mb-3">
                            Existiert für Ihr Haus bereits ein Online-Gutscheinprodukt?
                        </label>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="existingVoucherNo" :value="false" class="custom-control-input" v-model="voucherRequest.existingVoucher">
                            <label class="custom-control-label" for="existingVoucherNo">
                                Nein
                            </label>
                        </div>

                        <div class="custom-control custom-radio">
                            <input type="radio" id="existingVoucherYes" :value="true" class="custom-control-input" v-model="voucherRequest.existingVoucher">
                            <label class="custom-control-label" for="existingVoucherYes">
                                Ja
                            </label>
                        </div>

                        <!-- Existing voucher url -->
                        <input type="text" class="form-control mt-2" v-model="voucherRequest.existingVoucherUrl" placeholder="z.B. https://beispiel.de/gutscheinprodukt" v-if="voucherRequest.existingVoucher" />
                    </div>

                    <div class="d-flex mt-3">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <button type="button" class="btn btn-primary btn-lg arrow-right px-5 ml-auto next-button" @click="next">
                            {{ 'partners.hotels.form.next' | trans }}
                            <i class="wa-icon-long-arrow-right"></i>
                        </button>
                    </div>
                </div>

                <!-- Summary -->
                <div class="step--summary fadeIn wow" v-show="currentStep === 'summary'">
                    <h2 class="heading">
                        Zusammenfassung
                    </h2>

                    <p>
                        Bitte überprüfen Sie Ihre Angaben und klicken Sie anschließend auf <strong>Absenden</strong>. Unser Team wird Ihr Produkt überprüfen und sich kurzfristig bei Ihnen melden.
                    </p>

                    <!-- Errors -->
                    <div class="alert alert-danger form-errors" v-if="Object.keys(errors).length > 0">
                        <ul class="my-0" v-for="errorKey in Object.keys(errors)">
                            <li v-for="error in errors[errorKey]">
                                {{ error }}
                            </li>
                        </ul>
                    </div>

                    <div class="divider"></div>

                    <div class="row">
                        <div class="col-lg-6">
                            <div class="bg-light-teal my-4 p-4 rounded">
                                <h5>
                                    Wie können wir Sie erreichen?
                                </h5>

                                <p>
                                    Unser Team wird sich in Kürze mit Ihnen in Verbindung setzen.
                                </p>

                                <!-- Fullname -->
                                <div class="form-group">
                                    <label for="fullname">
                                        Vollständiger name
                                    </label>

                                    <input type="text" class="form-control" id="fullname" v-model="contactPerson.fullname" placeholder="z.B. Max Mustermann" />
                                </div>

                                <!-- Email address -->
                                <div class="form-group">
                                    <label for="email">
                                        E-Mail-Adresse
                                    </label>

                                    <input type="email" class="form-control" id="email" v-model="contactPerson.email" placeholder="z.B. max.mustermann@beispiel.de" />
                                </div>

                                <!-- Phone -->
                                <div class="form-group">
                                    <label for="phone">
                                        Telefonnummer
                                    </label>

                                    <input type="tel" class="form-control" id="phone" v-model="contactPerson.phone" placeholder="z.B. 03748 37682358" />
                                </div>
                            </div>

                            <div class="divider"></div>

                            <div class="form-group">
                                <label>
                                    Nachricht
                                    <small>
                                        (optional)
                                    </small>
                                </label>

                                <textarea class="form-control" rows="5" placeholder="Nachricht eingeben..." v-model="message"></textarea>
                            </div>
                        </div>

                        <div class="col-lg-6">
                            <!-- Hotel details -->
                            <div class="form-group">
                                <label>
                                    Unterkunftdetails
                                </label>
                            </div>
                            <div class="card border mb-3" v-if="hotel">
                                <div class="card-body">
                                    <button type="button" class="btn btn-link btn-sm float-right" @click="editHotel">
                                        <i class="fa fa-pencil"></i>
                                        {{ 'partners.hotels.form.edit' | trans }}
                                    </button>

                                    <h3 class="card-title">
                                        {{ hotel.name }}
                                    </h3>
                                    <p class="card-text">
                                        {{ hotel.subname }}
                                    </p>
                                </div>
                            </div>

                            <table>
                                <!-- Selected product types -->
                                <tr>
                                    <th>
                                        Bewerben als
                                    </th>
                                    <td class="text-right">
                                        <span v-for="productType in productTypes" class="px-2">
                                            <i class="font-weight-400" :class="$lodash.find(possibleProductTypes, { type: productType }).icon"></i>

                                            {{ $lodash.find(possibleProductTypes, { type: productType }).title }}
                                        </span>
                                    </td>
                                </tr>

                                <!-- Period -->
                                <tr v-if="flashSale.period === 'period'">
                                    <th>
                                        Bewerbungszeitraum
                                    </th>

                                    <td>
                                        {{ flashSale.from.format('Do MMM Y') }}
                                        -
                                        {{ flashSale.to.format('Do MMM Y') }}
                                    </td>
                                </tr>
                            </table>

                            <div v-if="rooms.length > 0">
                                <div class="divider"></div>

                                <!-- Components -->
                                <button type="button" class="btn btn-link btn-sm float-right mt-n1" @click="editComponents">
                                    <i class="fa fa-pencil"></i>
                                    {{ 'partners.hotels.form.edit' | trans }}
                                </button>

                                <div class="form-group">
                                    <label>
                                        Bausteine
                                    </label>
                                </div>

                                <!-- Single room -->
                                <small class="d-block text-muted my-2 text-center" v-if="rooms.length > 0">
                                    Zimmer
                                </small>
                                <div class="card border mb-2" v-for="room in rooms">
                                    <div class="card-body d-flex align-items-center">
                                        <i class="fa fa-bed font-weight-100 mr-3" style="font-size: 24px;"></i>

                                        <div class="flex-grow-1">
                                            <!-- Name -->
                                            <strong class="d-block">
                                                {{  room.name }}
                                            </strong>

                                            <!-- Details -->
                                            <small class="d-block text-muted">
                                                <!-- Occupancy -->
                                                {{ room.occupancy.maxPersons }} Personen

                                                <!-- Min + max price -->
                                                <span v-if="room.availabilities.length > 0">
                                                    &bull;
                                                    {{ $lodash.minBy(room.availabilities, 'price') | formatPrice }}
                                                    bis
                                                    {{ $lodash.maxBy(room.availabilities, 'price') | formatPrice }}
                                                    &euro;
                                                </span>

                                                <!-- Images -->
                                                <span v-if="room.images.length > 0">
                                                    &bull;
                                                    {{ room.images.length }}
                                                    <i class="fa fa-images"></i>
                                                </span>
                                            </small>
                                        </div>

                                        <button type="button" class="btn btn-link" title="Bearbeiten" @click="editRoom(room)">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>

                                <!-- Single board type -->
                                <small class="d-block text-muted mt-4 mb-2 text-center" v-if="boardTypes.length > 0">
                                    Zusätzliche Verpflegung
                                </small>
                                <div class="card border mb-2" v-for="boardType in boardTypes">
                                    <div class="card-body d-flex align-items-center">
                                        <i class="fa fa-utensils font-weight-100 mr-3" style="font-size: 24px;"></i>

                                        <div class="flex-grow-1">
                                            <!-- Name -->
                                            <strong class="d-block">
                                                {{  boardType.name }}

                                                <small class="text-muted ml-1">
                                                    {{  boardType.code }}
                                                </small>
                                            </strong>

                                            <!-- Details -->
                                            <small class="d-block text-muted">
                                                <!-- Extra charge -->
                                                <span v-if="boardType.extraCharge > 0">
                                                    {{ boardType.extraCharge | formatPrice }}
                                                    &euro;
                                                </span>

                                                <!-- Weekdays -->
                                                <span v-if="! (boardType.isMonday && boardType.isTuesday && boardType.isWednesday && boardType.isThursday && boardType.isFriday && boardType.isSaturday && boardType.isSunday)">
                                                    &bull;
                                                    {{ weekdaysList(boardType) }}
                                                </span>
                                            </small>

                                            <!-- Description -->
                                            <small class="d-block text-muted" v-if="boardType.description">
                                                {{  boardType.description }}
                                            </small>
                                        </div>

                                        <button type="button" class="btn btn-link" title="Bearbeiten" @click="editBoardType(boardType)">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>

                                <!-- Single additional service -->
                                <small class="d-block text-muted mt-4 mb-2 text-center" v-if="additionalServices.length > 0">
                                    Extras
                                </small>
                                <div class="card border mb-2" v-for="additionalService in additionalServices">
                                    <div class="card-body d-flex align-items-center">
                                        <!-- Image -->
                                        <img class="rounded mr-3" style="width: 70px; max-height: 70px;" :src="additionalService.image.url" v-if="additionalService.image" />

                                        <!-- Icon -->
                                        <i class="fa fa-cube font-weight-100 mr-3" style="font-size: 24px;" v-if="! additionalService.image"></i>

                                        <div class="flex-grow-1">
                                            <!-- Name -->
                                            <strong class="d-block">
                                                {{  additionalService.name }}
                                            </strong>
                                            <small class="d-block">
                                                {{ additionalService.subtitle }}
                                            </small>

                                            <!-- Details -->
                                            <small class="d-block text-muted">
                                                <!-- Price -->
                                                {{ additionalService.price | formatPrice }}
                                                &euro;

                                                <!-- Amount type -->
                                                <span v-if="additionalService.amountType === 'person'">
                                                    pro Person
                                                </span>
                                                <span v-if="additionalService.amountType === 'booking'">
                                                    pro Buchung
                                                </span>
                                            </small>
                                        </div>

                                        <button type="button" class="btn btn-link" title="Bearbeiten" @click="editAdditionalService(additionalService)">
                                            <i class="fa fa-pencil"></i>
                                        </button>
                                    </div>
                                </div>

                                <div class="divider"></div>

                                <!-- Notification + Billing -->
                                <div class="form-group">
                                    <label>
                                        Abwicklung
                                    </label>
                                </div>

                                <div class="row">
                                    <!-- Notification -->
                                    <div class="col-sm-6">
                                        <strong class="d-block">
                                            Avisierung per
                                            <span v-if="notification.type === 'single'">
                                                E-Mail
                                            </span>
                                            <span v-if="notification.type === 'bookingList'">
                                                Buchungsliste
                                            </span>
                                        </strong>

                                        an
                                        {{ notification.email }}

                                        <button type="button" class="btn btn-link btn-sm btn-block px-2 ml-n2 mt-2 text-left" @click="editNotification">
                                            <i class="fa fa-pencil"></i>
                                            {{ 'partners.hotels.form.edit' | trans }}
                                        </button>
                                    </div>

                                    <!-- Billing -->
                                    <div class="col-sm-6">
                                        <strong class="d-block">
                                            Abrechnung per
                                            <span v-if="billing.type === 'payout'">
                                                Gutschrift
                                            </span>
                                            <span v-if="billing.type === 'invoice'">
                                                Rechnung
                                            </span>
                                        </strong>

                                        <span v-if="billing.type === 'payout'">
                                            <small class="d-block text-muted">
                                                Beleg senden an
                                            </small>
                                            <span v-if="billing.isSameEmail">
                                                {{ notification.email }}
                                            </span>
                                            <span v-if="! billing.isSameEmail">
                                                {{ billing.email }}
                                            </span>

                                            <small class="d-block text-muted">
                                                Auszahlungskonto
                                            </small>
                                            {{ billing.iban }}
                                        </span>

                                        <button type="button" class="btn btn-link btn-sm btn-block px-2 ml-n2 mt-2 text-left" @click="editBilling">
                                            <i class="fa fa-pencil"></i>
                                            {{ 'partners.hotels.form.edit' | trans }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Voucher request -->
                            <div v-if="productTypes.indexOf('voucher') !== -1">
                                <div class="divider"></div>

                                <button type="button" class="btn btn-link btn-sm float-right mt-n1" @click="editVoucherRequest">
                                    <i class="fa fa-pencil"></i>
                                    {{ 'partners.hotels.form.edit' | trans }}
                                </button>

                                <div class="form-group">
                                    <label>
                                        Gutschein-Anfrage
                                    </label>
                                </div>

                                <div class="row">
                                    <!-- Redemption -->
                                    <div class="col-md-4">
                                        <strong class="d-block">
                                            Einlösung
                                        </strong>

                                        <span v-if="voucherRequest.redemptionType === 'availability'">
                                            per Kontingent
                                        </span>
                                        <span v-if="voucherRequest.redemptionType === 'request'">
                                            per Anruf
                                        </span>
                                    </div>

                                    <!-- Validity -->
                                    <div class="col-md-4">
                                        <strong class="d-block">
                                            Gültigkeit
                                        </strong>

                                        <span v-if="voucherRequest.validity === 'oneYear'">
                                            1 Jahr
                                        </span>
                                        <span v-if="voucherRequest.redemptionType === 'lessThanAYear'">
                                            Weniger als ein Jahr
                                        </span>
                                        <span v-if="voucherRequest.redemptionType === 'moreThanAYear'">
                                            Mehr als ein Jahr
                                        </span>
                                    </div>

                                    <!-- Existing voucher -->
                                    <div class="col-md-4">
                                        <strong class="d-block">
                                            Gutschein vorhanden?
                                        </strong>

                                        <span v-if="voucherRequest.existingVoucher">
                                            Ja
                                        </span>
                                        <span v-if="! voucherRequest.existingVoucher">
                                            Nein
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="divider"></div>

                    <!-- Accept data protection -->
                    <div class="form-check form-check-border">
                        <input type="checkbox" class="form-check-input" id="acceptDataProtection" v-model="isAcceptDataProtection">
                        <label class="form-check-label" for="acceptDataProtection">
                            Ich stimme der
                            <a :href="$window.route('frontend.legal.dataProtection')" target="_blank">
                                Datenschutzerklärung
                            </a>
                            der watado GmbH zu.
                        </label>
                    </div>

                    <div class="d-flex mt-4">
                        <button type="button" class="btn btn-link btn-lg px-3 ml-n3" @click="prev">
                            {{ 'partners.hotels.form.back' | trans }}
                        </button>

                        <!-- Submit button -->
                        <button type="button" class="btn btn-primary book-action btn-lg px-5 ml-auto" @click="submit" :disabled="isLoading">
                            <span v-if="! isLoading">Absenden</span>
                            <i class="fa fa-spinner fa-spin" v-if="isLoading"></i>

                            <span v-if="isLoading && loadingInfo">
                                {{ loadingInfo }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Help -->
            <div class="col-lg-6 help" v-if="currentStep !== 'summary'">
                <!-- Indicator -->
                <ul class="steps-indicator d-none d-md-flex">
                    <li v-for="(step, index) in steps" :class="{ active: steps.indexOf(currentStep) === index, done: steps.indexOf(currentStep) > index }">
                        <i class="fa fa-check font-weight-400" v-if="steps.indexOf(currentStep) > index"></i>
                        <span v-if="steps.indexOf(currentStep) <= index">{{ index + 1 }}</span>
                    </li>
                </ul>

                <div class="inner">
                    <!-- Arrow up -->
                    <button type="button" class="btn btn-link btn-block d-md-none my-2 py-4" @click="scrollTo('.steps')">
                        <i class="fa fa-chevron-up"></i>
                    </button>

                    <!-- Steps count -->
                    <span class="steps-count">
                        {{ 'partners.hotels.form.help' | trans }}
                        -
                        Schritt
                        {{ steps.indexOf(currentStep) + 1 }}
                        von
                        {{ steps.length }}
                    </span>

                    <!-- Help -->
                    <!-- Details -->
                    <div class="fadeInUp wow" v-show="currentStep === 'details'">
                        <h3>
                            Hoteldaten eingeben
                        </h3>

                        <p>
                            Bitte geben Sie den Namen Ihrer Unterkunft ein und wählen Sie Ihr Haus in der Trefferliste aus. Alternativ können Sie alle Daten auch manuell eingeben.
                        </p>
                        <p>
                            <i class="fa fa-info mr-1"></i>
                            Eine Telefonnummer oder E-Mail-Adresse, für Rückfragen zu Ihrer Anfrage, können Sie im letzten Schritt angeben.
                        </p>

                        <img class="img-fluid mx-auto mt-5" style="max-height: 500px;" :src="$cdn.default + 'illustrations/search.svg?raw=true'" />
                    </div>

                    <!-- Product types -->
                    <div class="fadeInUp wow" v-show="currentStep === 'productTypes'">
                        <h3>
                            Gewünschte Produktart bestimmen
                        </h3>

                        <p>
                            Wir bieten Ihnen mehrere Möglichkeiten Ihr Haus online Vertriebspartnern und Kunden zu präsentieren. Ein Must-Have ist das Arrangement, welches dauerhaft Stammkunden knüpft. Flash-Sales eignen sich hervorragend um in kurzer Zeit Aufmerksamkeit und somit Buchungen zu generieren.
                        </p>
                        <p>
                            Zusätzlich bieten wir die Möglichkeit klassisch die reine Übernachtung + Verpflegung zu vermarkten oder aber ausschließlich paketiert oder in Form eines Gutscheines vermarktet zu werden.
                        </p>

                        <img class="img-fluid mx-auto mt-5" style="max-height: 500px;" :src="$cdn.default + 'illustrations/blogging.svg?raw=true'" />
                    </div>

                    <!-- Components -->
                    <div class="fadeInUp wow" v-show="currentStep === 'components'">
                        <h5>
                            Zimmer
                        </h5>

                        <p>
                            Bieten Sie mehrere Zimmerkategorien an und bestimmen Sie pro Zimmer die bereits inkludierte Verpflegung. Sie haben die Möglichkeit hier neben Kontingenten bereits Sperrtermine zu hinterlegen. Bitte laden Sie ebenfalls 1-2 Bilder pro Zimmer hoch und fügen Sie Inklusivleistungen hinzu.
                        </p>

                        <div class="divider transparent-divider"></div>

                        <h5>
                            Zusätzliche Verpflegung
                        </h5>

                        <p>
                            Zu der bereits inkludierten Verpflegung im Zimmer können Sie den Gästen gegen einen Aufpreis weitere Verpflegungsarten anbieten. Sollten Sie abweichende Aufpreise pro Wochentag haben, so können Sie die gleiche Verpflegungsart mehrfach hinzufügen und bei der <strong>Verfügbarkeit</strong> die gewünschten Wochentage auswählen.
                        </p>

                        <div class="divider transparent-divider"></div>

                        <h5>
                            Extras
                        </h5>

                        <p>
                            Bietet Ihr Haus beispielsweise Massagen, Anwendungen oder Dienstleistungen an, so können Sie diese dem Gast als Zusatzleistung anbieten. So generieren Sie bereits vor dem Aufenthalt zusätzlichen Umsatz.
                        </p>
                    </div>

                    <!-- Notification -->
                    <div class="fadeInUp wow" v-show="currentStep === 'notification'">
                        <h3>
                            Benachrichtigungen
                        </h3>

                        <p>
                            Standardmäßig erhalten Sie zu jeder neuen Buchung, Umbuchung oder Stornierung eine einzelne E-Mail, in welcher Sie die Buchung/Änderung bestätigen müssen. Die zusätzliche Bestätigung soll Überbuchungen verhindern.
                        </p>
                        <p>
                            Mit <strong>Buchungslisten</strong> erhalten Sie von uns jeden Montag früh automatisch eine Liste zur Kontrolle mit allen Anreisen der kommenden Woche.
                        </p>

                        <img class="img-fluid mx-auto mt-5" style="max-height: 500px;" :src="$cdn.default + 'illustrations/study-terms-of-service.svg?raw=true'" />
                    </div>

                    <!-- Billing -->
                    <div class="fadeInUp wow" v-show="currentStep === 'billing'">
                        <h3>
                            Schnelle und zuverlässige Verrechnung.
                        </h3>

                        <h5>
                            Gutschriften
                        </h5>

                        <p>
                            Bei <strong>Gutschriften</strong> erhalten Sie Ihr Geld bis zum 5. Werktag des Folgemonats automatisch auf Ihr Bankkonto überwiesen. Der große Vorteil hierbei ist, dass diese Gutschriften zu 100 % von unserem System automatisch erstellt und ausgezahlt werden. Die Erfahrung zeigte uns, dass bei dieser Art der Verrechnung der geringste Aufwand für beide Seiten entsteht.
                        </p>

                        <div class="divider transparent-divider"></div>

                        <h5>
                            Rechnung
                        </h5>

                        <p>
                            Wenn Sie Rechnungen von Ihrem Haus an unsere Buchhaltung senden, so werden diese innerhalb von einer Woche kontrolliert und ausgezahlt. Korrekturen erfordern hierbei die erneute Kontrolle von beiden Seiten.
                        </p>

                        <img class="img-fluid mx-auto mt-5" style="max-height: 400px;" :src="$cdn.default + 'illustrations/attach-value-coupon.svg?raw=true'" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Edit mode -->
        <div class="container edit" v-if="currentEdit && ! isSuccess">
            <div class="row">
                <div class="col-lg-3">
                    <!-- Save edit desktop -->
                    <button type="button" class="btn btn-link d-none d-md-block" @click="saveEdit">
                        <i class="fa fa-chevron-left mr-2"></i>
                        Speichern & zurück
                    </button>

                    <!-- Save edit mobile -->
                    <button type="button" class="btn btn-primary d-md-none btn-lg next-button arrow-right px-5" @click="saveEdit">
                        <i class="fa fa-chevron-left mr-2"></i>
                        Speichern & zurück
                    </button>

                    <!-- Checklist -->
                    <!-- Room checklist -->
                    <div class="bg-light-gray rounded mt-3 px-4 py-3" v-if="currentEdit === 'room'">
                        <h6>
                            Erforderlich
                        </h6>
                        <ul class="checklist">
                            <li :class="{ checked: validRoomInfo(editTarget) }">
                                Informationen
                            </li>
                            <li :class="{ checked: validBoardType(editTarget) }">
                                Verpflegung
                            </li>
                            <li :class="{ checked: validRoomOccupancy(editTarget) }">
                                Belegung
                            </li>
                            <li :class="{ checked: validRoomAvailabilities(editTarget) }">
                                Preis & Kontingent
                            </li>
                            <li :class="{ checked: validRoomCancellationRules(editTarget) }">
                                Stornoregeln
                            </li>
                        </ul>

                        <h6 class="mt-4">
                            Optional
                        </h6>
                        <ul class="checklist">
                            <li :class="{ checked: validRoomStopSales(editTarget) }">
                                Sperrtermine
                            </li>
                            <li :class="{ checked: validRoomIncludedServices(editTarget) }">
                                Inklusivleistungen
                            </li>
                        </ul>
                    </div>

                    <!-- Board type checklist -->
                    <div class="bg-light-gray rounded mt-3 px-4 py-3" v-if="currentEdit === 'boardType'">
                        <h6>
                            Erforderlich
                        </h6>
                        <ul class="checklist">
                            <li :class="{ checked: validBoardTypeInfo(editTarget) }">
                                Informationen
                            </li>
                            <li :class="{ checked: validBoardTypeExtraCharge(editTarget) }">
                                Aufschlag
                            </li>
                            <li :class="{ checked: validBoardTypeAvailability(editTarget) }">
                                Verfügbarkeit
                            </li>
                        </ul>
                    </div>

                    <!-- Additional service checklist -->
                    <div class="bg-light-gray rounded mt-3 px-4 py-3" v-if="currentEdit === 'additionalService'">
                        <h6>
                            Erforderlich
                        </h6>
                        <ul class="checklist">
                            <li :class="{ checked: validAdditionalServiceInfo(editTarget) }">
                                Informationen
                            </li>
                            <li :class="{ checked: validAdditionalServiceDescription(editTarget) }">
                                Beschreibung
                            </li>
                            <li :class="{ checked: validAdditionalServicePrice(editTarget) }">
                                Preis
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="col-lg-7 offset-lg-1">
                    <!-- Edit room -->
                    <div class="edit-room" v-if="currentEdit === 'room'">
                        <h2>
                            Zimmer bearbeiten
                        </h2>
                        <p class="lead">
                            Bitte geben Sie alle erforderlichen Informationen an.
                        </p>

                        <!-- Progress bar -->
                        <div class="progress mb-4">
                            <div class="progress-bar font-weight-900" :class="{ 'bg-success': roomProgress(editTarget) === 100 }" role="progressbar" :style="{ width: roomProgress(editTarget) + '%' }" :aria-valuenow="roomProgress(editTarget)" aria-valuemin="0" aria-valuemax="100">
                                {{ roomProgress(editTarget) }}
                                %
                            </div>
                        </div>

                        <!-- Name -->
                        <div class="form-group">
                            <label for="name">
                                Name
                                <span class="required">*</span>
                            </label>

                            <input type="text" v-model="editTarget.name" class="form-control" id="roomName" placeholder="Zimmername eingeben" />
                            <small class="d-block text-muted mt-1">
                                Beispiele: Doppelzimmer, Familienzimmer oder Superior Doppelzimmer
                            </small>
                        </div>

                        <!-- Add image -->
                        <div class="custom-file">
                            <input type="file" accept="image/*" class="custom-file-input" id="roomImage" multiple @change="onRoomImageChange">
                            <label class="custom-file-label btn btn-link text-left text-primary" for="roomImage" style="font-size: 14px;">
                                <i class="fa fa-plus mr-1"></i>
                                Zimmerbild hinzufügen
                            </label>
                        </div>

                        <!-- Images -->
                        <div v-if="editTarget.images.length > 0">
                            <div class="card border d-inline-block mr-2 mt-2" v-for="(image, imageIndex) in editTarget.images" style="width: 140px; max-height: 140px; vertical-align: top;">
                                <!-- Remove image -->
                                <button type="button" class="btn btn-link text-white" style="position: absolute; right: 0; top: 0;" @click="removeRoomImage(imageIndex)">
                                    <i class="fa fa-trash"></i>
                                </button>

                                <img :src="image.url" class="card-img-top" />
                            </div>
                        </div>

                        <div class="divider"></div>

                        <!-- Board type -->
                        <div class="form-group">
                            <label for="name">
                                Inkludierte Verpflegung
                                <span class="required">*</span>
                            </label>

                            <div class="custom-input-group">
                                <select class="custom-select" v-model="editTarget.boardType">
                                    <option disabled :value="null">
                                        Bitte wählen...
                                    </option>
                                    <option :value="boardType" v-for="boardType in possibleBoardTypes">
                                        {{ boardType.name }}
                                        ({{ boardType.code }})
                                    </option>
                                </select>
                            </div>
                            <small class="d-block text-muted mt-1">
                                Wählen Sie <strong class="text-primary" @click="() => {
                                    let roomOnly = $lodash.find(possibleBoardTypes, { name: 'Nur Übernachtung' })
                                    if (roomOnly) {
                                        editTarget.boardType = roomOnly
                                    }
                                }">Nur Übernachtung</strong>, falls keine Verpflegung inkludiert ist.
                            </small>
                        </div>

                        <div class="divider"></div>

                        <!-- Included services -->
                        <div class="form-group">
                            <label for="includedService">
                                Inklusivleistungen
                            </label>

                            <!-- List existing included services -->
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item" v-for="(item, index) in editTarget.includedServices">
                                    <div class="d-flex w-100 justify-content-between">
                                        <span class="mr-2">
                                            {{ item.name }}
                                        </span>

                                        <button type="button" class="btn btn-link" @click="removeIncludedService(index)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </li>
                            </ul>

                            <div class="row">
                                <div class="col-md-8">
                                    <input type="text" v-model="newIncludedService" class="form-control" id="includedService" placeholder="Inklusivleistung eingeben" />
                                </div>
                                <div class="col-md-4">
                                    <button type="button" class="btn btn-primary btn-block btn-lg" @click="addIncludedService" :disabled="newIncludedService.length < 4">
                                        {{ 'partners.hotels.form.add' | trans }}
                                    </button>
                                </div>
                            </div>

                            <small class="d-block text-muted mt-1">
                                Beispiele: <strong>Welcome Drink</strong> oder <strong>Tägliche Nutzung vom Spa-Bereich</strong>
                            </small>
                        </div>

                        <div class="divider"></div>

                        <!-- Occupancy -->
                        <label class="d-block">
                            Grundbelegung
                            <span class="required">*</span>
                            <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Bitte geben Sie an, welche Grundbelegung (ohne Ermäßigung/Aufschlag) möglich ist."></i>
                        </label>

                        <table class="mt-3">
                            <tr>
                                <th></th>
                                <th class="text-center">Minimum</th>
                                <th class="text-center">Maximum</th>
                            </tr>
                            <tr>
                                <th class="pr-2">Erwachsene</th>
                                <td class="p-2">
                                    <input type="text" class="form-control text-center" v-model="editTarget.occupancy.minAdults" placeholder="z.B. 1" />
                                </td>
                                <td class="p-2">
                                    <input type="text" class="form-control text-center" v-model="editTarget.occupancy.maxAdults" placeholder="z.B. 2" />
                                </td>
                            </tr>
                            <tr>
                                <th class="pr-2">Kinder</th>
                                <td class="p-2">
                                    <input type="text" class="form-control text-center" v-model="editTarget.occupancy.minChildren" placeholder="z.B. 0" />
                                </td>
                                <td class="p-2">
                                    <input type="text" class="form-control text-center" v-model="editTarget.occupancy.maxChildren" placeholder="z.B. 1" />
                                </td>
                            </tr>
                            <tr>
                                <th class="pr-2 border-top border-light">Personen</th>
                                <td class="p-2 border-top border-light">
                                    <input type="text" class="form-control text-center" v-model="editTarget.occupancy.minPersons" placeholder="z.B. 1" />
                                </td>
                                <td class="p-2 border-top border-light">
                                    <input type="text" class="form-control text-center" v-model="editTarget.occupancy.maxPersons" placeholder="z.B. 2" />
                                </td>
                            </tr>
                        </table>

                        <!-- Additional persons -->
                        <button type="button" class="btn btn-link btn-sm mt-3" @click="toggleRoomAdditionalPersons(editTarget)">
                            <!-- Disabled -->
                            <span v-if="! editTarget.isAdditionalPersons">
                                <i class="fa fa-plus mr-1"></i>
                                Zusätzliche Personen
                            </span>

                            <!-- Enabled -->
                            <span v-if="editTarget.isAdditionalPersons">
                                <i class="fa fa-minus mr-1"></i>
                                Zusätzliche Personen deaktivieren
                            </span>
                        </button>

                        <!-- Additional persons -->
                        <div class="mt-3" v-if="editTarget.isAdditionalPersons">
                            <div class="divider mt-0"></div>

                            <label class="d-block">
                                Zusätzliche Personen
                                <span class="required">*</span>
                                <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Über die Grundbelegung hinaus können zusätzliche Personen definiert werden, für welche ebenfalls Aufschläge/Ermäßigungen hinterlegt werden können."></i>
                            </label>

                            <table class="mt-3">
                                <tr>
                                    <th></th>
                                    <th class="text-center">Maximum</th>
                                </tr>
                                <tr>
                                    <th class="pr-2">Erwachsene</th>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.occupancy.maxAdditionalAdults" placeholder="z.B. 1" />
                                    </td>
                                </tr>
                                <tr>
                                    <th class="pr-2">Kinder</th>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.occupancy.maxAdditionalChildren" placeholder="z.B. 2" />
                                    </td>
                                </tr>
                                <tr>
                                    <th class="pr-2 border-top border-light">Personen</th>
                                    <td class="p-2 border-top border-light">
                                        <input type="text" class="form-control text-center" v-model="editTarget.occupancy.maxAdditionalPersons" placeholder="z.B. 2" />
                                    </td>
                                </tr>
                            </table>
                        </div>

                        <div class="divider"></div>

                        <label class="d-block">
                            Preis / Kontingent
                            <span class="required">*</span>
                            <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Bitte geben Sie alle Preise in netto und exkl. City Tax an."></i>
                        </label>

                        <!-- Availabilities table -->
                        <table class="w-100">
                            <thead>
                                <tr>
                                    <th>
                                        Zeitraum
                                    </th>
                                    <th class="text-center">
                                        Min. Nächte
                                    </th>
                                    <th class="text-center">
                                        Kontingent
                                        <small class="d-block">
                                            pro Tag
                                        </small>
                                    </th>
                                    <th class="text-center">
                                        Release
                                        <small class="d-block">
                                            in Tagen
                                        </small>
                                    </th>
                                    <th class="text-center">
                                        Zimmerpreis
                                        <small class="d-block">
                                            pro Nacht in &euro;
                                        </small>
                                    </th>
                                    <th class="text-center">
                                        Einzelperson-<br />
                                        aufschlag in %
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>

                            <!-- Entries -->
                            <tbody v-for="(availability, index) in editTarget.availabilities">
                                <tr>
                                    <!-- Period -->
                                    <td>
                                        <div class="dropdown" :class="{ open: showDropdown === ('availabilityPeriod' + index) }">
                                            <button class="btn dropdown-toggle" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="toggleDropdown('availabilityPeriod' + index);">
                                                <!-- From -->
                                                <span v-if="editTarget.availabilities[index].dateRange.startDate !== null">
                                                    {{ $moment(editTarget.availabilities[index].dateRange.startDate).format('Do MMM') }}
                                                    -
                                                </span>

                                                <!-- To -->
                                                <span v-if="editTarget.availabilities[index].dateRange.endDate !== null">
                                                    {{ $moment(editTarget.availabilities[index].dateRange.endDate).format('Do MMM Y') }}
                                                </span>

                                                <!-- Placeholder -->
                                                <span v-if="editTarget.availabilities[index].dateRange.endDate === null && editTarget.availabilities[index].dateRange.endDate === null">
                                                    Zeitraum wählen...
                                                </span>
                                            </button>

                                            <div class="dropdown-menu period-picker px-3" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === ('availabilityPeriod' + index) }">
                                                <input type="hidden" :id="'bookingPeriodPicker' + index" />
                                                <div class="calendar" :id="'calendar' + index"></div>

                                                <date-range-picker
                                                    ref="picker"
                                                    opens="inline"
                                                    :ranges="false"
                                                    :show-week-numbers="true"
                                                    :locale-data="datePickerLocaleData"
                                                    :showWeekNumbers="true"
                                                    :showDropdowns="true"
                                                    :autoApply="true"
                                                    v-model="editTarget.availabilities[index].dateRange"
                                                    :linkedCalendars="true"
                                                    @update="() => cancelAll()"
                                                ></date-range-picker>

                                                <!-- Define weekdays -->
                                                <div class="divider my-3"></div>

                                                <div class="px-3">
                                                    <label class="d-block">Wochentage</label>

                                                    <!-- Monday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_0_' + index" type="checkbox" v-model="editTarget.availabilities[index].isMonday" />
                                                        <label :for="'weekdays_0_' + index">
                                                            Montag
                                                        </label>
                                                    </div>

                                                    <!-- Tuesday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_1_' + index" type="checkbox" v-model="editTarget.availabilities[index].isTuesday" />
                                                        <label :for="'weekdays_1_' + index">
                                                            Dienstag
                                                        </label>
                                                    </div>

                                                    <!-- Wednesday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_2_' + index" type="checkbox" v-model="editTarget.availabilities[index].isWednesday" />
                                                        <label :for="'weekdays_2_' + index">
                                                            Mittwoch
                                                        </label>
                                                    </div>

                                                    <!-- Thursday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_3_' + index" type="checkbox" v-model="editTarget.availabilities[index].isThursday" />
                                                        <label :for="'weekdays_3_' + index">
                                                            Donnerstag
                                                        </label>
                                                    </div>

                                                    <!-- Friday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_4_' + index" type="checkbox" v-model="editTarget.availabilities[index].isFriday" />
                                                        <label :for="'weekdays_4_' + index">
                                                            Freitag
                                                        </label>
                                                    </div>

                                                    <!-- Saturday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_5_' + index" type="checkbox" v-model="editTarget.availabilities[index].isSaturday" />
                                                        <label :for="'weekdays_5_' + index">
                                                            Samstag
                                                        </label>
                                                    </div>

                                                    <!-- Sunday -->
                                                    <div class="form-group d-inline-block mr-2">
                                                        <input :id="'weekdays_6_' + index" type="checkbox" v-model="editTarget.availabilities[index].isSunday" />
                                                        <label :for="'weekdays_6_' + index">
                                                            Sonntag
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.availabilities[index].minNights" style="min-width: 50px;" />
                                    </td>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.availabilities[index].amount" style="min-width: 50px;" />
                                    </td>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.availabilities[index].release" style="min-width: 50px;" />
                                    </td>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.availabilities[index].price" style="min-width: 50px;" />
                                    </td>
                                    <td class="p-2">
                                        <input type="text" class="form-control text-center" v-model="editTarget.availabilities[index].singlePersonExtraCharge" style="min-width: 50px;" />
                                    </td>
                                    <td>
                                        <button type="button" class="btn btn-link" @click="removeAvailability(index)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>

                            <tr v-if="editTarget.availabilities.length === 0">
                                <td colspan="6">
                                    <div class="alert alert-info my-1">
                                        Noch kein Eintrag vorhanden.
                                    </div>
                                </td>
                            </tr>
                        </table>

                        <!-- Add availability -->
                        <button type="button" class="btn btn-link btn-sm" @click="addAvailability">
                            <i class="fa fa-plus mr-1"></i>
                            Kontingent hinzufügen
                        </button>

                        <!-- Stop sales table -->
                        <div class="flash animated stop-sales" v-if="editTarget.stopSales.length > 0">
                            <div class="divider"></div>

                            <label class="d-block mb-3">
                                Sperrtermine
                            </label>

                            <table class="w-100">
                                <thead>
                                    <tr>
                                        <th>
                                            Zeitraum
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <!-- Entries -->
                                <tbody v-for="(stopSale, index) in editTarget.stopSales">
                                    <tr>
                                        <!-- Period -->
                                        <td>
                                            <!-- From -->
                                            <span v-if="editTarget.stopSales[index].from !== null">
                                                {{ editTarget.stopSales[index].from.format('Do MMM') }}
                                                -
                                            </span>

                                            <!-- To -->
                                            <span v-if="editTarget.stopSales[index].to !== null">
                                                {{ editTarget.stopSales[index].to.format('Do MMM Y') }}
                                            </span>
                                        </td>
                                        <td class="text-right">
                                            <button type="button" class="btn btn-link" @click="removeStopSale(index)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Add stop sale -->
                        <div class="position-relative" :class="{ open: showDropdown === 'addStopSale' }">
                            <button class="btn btn-link btn-sm dropdown-toggle" type="button" id="dropdownMenuButton" aria-haspopup="true" aria-expanded="false" @click="toggleDropdown('addStopSale');">
                                <i class="fa fa-plus mr-1"></i>
                                Sperrtermin hinzufügen
                            </button>

                            <div class="dropdown-menu period-picker px-3" aria-labelledby="dropdownMenuButton" :class="{ show: showDropdown === 'addStopSale' }">
                                <input type="hidden" id="stopSalePeriodPicker" />
                                <div class="calendar" id="stopSaleCalendar"></div>
                            </div>
                        </div>


                        <!-- Extra charges for additional persons -->
                        <div v-if="editTarget.isAdditionalPersons">
                            <div class="divider"></div>

                            <label class="d-block">
                                Ermäßigung für zusätzliche Personen
                                <span class="required">*</span>
                            </label>

                            <p>
                                <i class="fa fa-info-circle mr-1"></i>
                                Definieren Sie die Ermäßigung pro zusätzlicher Person auf der Basis vom <strong>Zimmerpreis pro Person</strong>.
                            </p>

                            <table class="w-100">
                                <thead>
                                    <tr>
                                        <th>Typ</th>
                                        <th class="text-center">Altersspanne</th>
                                        <th class="text-center">Ermäßigung</th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <tbody v-for="(extraCharge, index) in editTarget.additionalPersonsExtraCharges">
                                    <tr>
                                        <td class="p-2">
                                            <div class="custom-input-group">
                                                <select class="custom-select" v-model="editTarget.additionalPersonsExtraCharges[index].type">
                                                    <option value="adult">
                                                        Erwachsener
                                                    </option>
                                                    <option value="child">
                                                        Kind
                                                    </option>
                                                </select>
                                            </div>
                                        </td>
                                        <td class="p-2">
                                            <!-- Show hide age selection -->
                                            <div v-if="editTarget.additionalPersonsExtraCharges[index].type === 'child'">
                                                <div class="custom-input-group d-inline-block">
                                                    <select class="custom-select" v-model="editTarget.additionalPersonsExtraCharges[index].from">
                                                        <option :value="age" v-for="age in [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17]">
                                                            {{ age }}
                                                        </option>
                                                    </select>
                                                </div>
                                                -
                                                <div class="custom-input-group d-inline-block">
                                                    <select class="custom-select" v-model="editTarget.additionalPersonsExtraCharges[index].to">
                                                        <option :value="age" v-for="age in [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17]">
                                                            {{ age }}
                                                        </option>
                                                    </select>
                                                </div>
                                            </div>

                                            <span class="text-muted" v-if="editTarget.additionalPersonsExtraCharges[index].type === 'adult'">
                                                -
                                            </span>
                                        </td>
                                        <td class="p-2">
                                            <!-- Value type -->
                                            <div class="form-group w-100">
                                                <div class="btn-group btn-group-toggle w-100">
                                                    <!-- Euro -->
                                                    <label class="btn btn-outline-primary btn-sm px-3" :for="'value_type_euro_' + index" :class="{ active: editTarget.additionalPersonsExtraCharges[index].valueType === 'euro' }" style="font-size: 14px;">
                                                        <input type="radio" value="euro" :id="'value_type_euro_' + index" autocomplete="off" v-model="editTarget.additionalPersonsExtraCharges[index].valueType">
                                                        Euro &euro;
                                                    </label>

                                                    <!-- Percent -->
                                                    <label class="btn btn-outline-primary btn-sm px-3" :for="'value_type_percent_' + index" :class="{ active: editTarget.additionalPersonsExtraCharges[index].valueType === 'percent' }" style="font-size: 14px;">
                                                        <input type="radio" value="percent" :id="'value_type_percent_' + index" autocomplete="off" v-model="editTarget.additionalPersonsExtraCharges[index].valueType">
                                                        Prozent %
                                                    </label>
                                                </div>
                                            </div>

                                            <!-- Value -->
                                            <input type="text" class="form-control text-center" v-model="editTarget.additionalPersonsExtraCharges[index].value" />
                                        </td>
                                        <td class="p-2">
                                            <button type="button" class="btn btn-link" @click="removeAdditionalPersonsExtraCharge(index)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>

                                <tr v-if="editTarget.additionalPersonsExtraCharges.length === 0">
                                <td colspan="4">
                                    <div class="alert alert-info my-1">
                                        Noch kein Eintrag vorhanden.
                                    </div>
                                </td>
                            </tr>
                            </table>

                            <!-- Add additional person extra charge -->
                            <button type="button" class="btn btn-link btn-sm" @click="addAdditionalPersonExtraCharge">
                                <i class="fa fa-plus mr-1"></i>
                                Eintrag hinzufügen
                            </button>
                        </div>

                        <div class="divider"></div>

                        <!-- Cancellation rules -->
                        <label class="d-block">
                            Stornoregeln
                            <span class="required">*</span>
                            <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Regeln Sie, wie viel der Gast bis x-Tage vor Anreise bezahlen muss."></i>
                        </label>

                        <table class="w-100">
                                <thead>
                                    <tr>
                                        <th>Tage vor Anreise</th>
                                        <th class="text-center">
                                            Zimmerpreis
                                            <small class="d-block">
                                                anteilig
                                            </small>
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>

                                <tbody v-for="(cancellationRule, index) in editTarget.cancellationRules">
                                    <tr>
                                        <td class="p-2">
                                            <!-- Days before -->
                                            <input type="text" class="form-control text-center" style="width: 100px;" v-model="editTarget.cancellationRules[index].daysBefore" />
                                        </td>
                                        <td class="p-2">
                                            <!-- Value type -->
                                            <div class="form-group w-100">
                                                <div class="btn-group btn-group-toggle w-100">
                                                    <!-- Euro -->
                                                    <label class="btn btn-outline-primary btn-sm px-3" :for="'cr_value_type_euro_' + index" :class="{ active: editTarget.cancellationRules[index].valueType === 'euro' }" style="font-size: 14px;">
                                                        <input type="radio" value="euro" :id="'cr_value_type_euro_' + index" autocomplete="off" v-model="editTarget.cancellationRules[index].valueType">
                                                        Euro &euro;
                                                    </label>

                                                    <!-- Percent -->
                                                    <label class="btn btn-outline-primary btn-sm px-3" :for="'cr_value_type_percent_' + index" :class="{ active: editTarget.cancellationRules[index].valueType === 'percent' }" style="font-size: 14px;">
                                                        <input type="radio" value="percent" :id="'cr_value_type_percent_' + index" autocomplete="off" v-model="editTarget.cancellationRules[index].valueType">
                                                        Prozent %
                                                    </label>
                                                </div>
                                            </div>

                                            <!-- Value -->
                                            <input type="text" class="form-control text-center" v-model="editTarget.cancellationRules[index].value" />
                                        </td>
                                        <td class="p-2">
                                            <button type="button" class="btn btn-link" @click="removeCancellationRule(index)">
                                                <i class="fa fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>

                                <tr v-if="editTarget.cancellationRules.length === 0">
                                <td colspan="3">
                                    <div class="alert alert-info my-1">
                                        Noch keine Stornoregel vorhanden.
                                    </div>
                                </td>
                            </tr>
                            </table>

                            <!-- Add cancellation rule -->
                            <button type="button" class="btn btn-link btn-sm" @click="addCancellationRule">
                                <i class="fa fa-plus mr-1"></i>
                                Eintrag hinzufügen
                            </button>
                    </div>

                    <!-- Edit board type -->
                    <div class="edit-board-type" v-if="currentEdit === 'boardType'">
                        <h2>
                            Verpflegungsart bearbeiten
                        </h2>
                        <p class="lead">
                            Bitte geben Sie alle erforderlichen Informationen an.
                        </p>

                        <!-- Progress bar -->
                        <div class="progress mb-4">
                            <div class="progress-bar font-weight-900" :class="{ 'bg-success': boardTypeProgress(editTarget) === 100 }" role="progressbar" :style="{ width: boardTypeProgress(editTarget) + '%' }" :aria-valuenow="boardTypeProgress(editTarget)" aria-valuemin="0" aria-valuemax="100">
                                {{ boardTypeProgress(editTarget) }}
                                %
                            </div>
                        </div>

                        <!-- Name -->
                        <div class="form-group">
                            <label for="name">
                                Name
                                <span class="required">*</span>
                            </label>

                            <input type="text" v-model="editTarget.name" class="form-control" id="boardTypeName" :disabled="editTarget.type_id !== null" placeholder="Verpflegungsart eingeben" />
                            <small class="d-block text-muted mt-1">
                                Beispiele: American Frühstück oder Sekt-Frühstück,
                            </small>
                        </div>

                        <!-- Description -->
                        <div class="form-group" v-if="editTarget.type_id === null">
                            <label for="description" style="font-size: 13px;">
                                Kurzbeschreibung
                                (optional)
                            </label>

                            <textarea rows="2" v-model="editTarget.description" id="description" class="form-control" :disabled="editTarget.type_id !== null" placeholder="Geben Sie eine kurze Beschreibung ein." />
                        </div>

                        <div class="divider"></div>

                        <!-- Extra charge -->
                        <label class="d-block">
                            Aufschlag pro Person und Nacht
                            <span class="required">*</span>
                            <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Bitte geben Sie den netto-Aufschlag an, welcher pro Person und pro Nacht zusätzlich berechnet werden soll."></i>
                        </label>

                        <div class="vertical-align-middle">
                            <label for="extraCharge">
                                Aufschlag
                            </label>

                            <input id="extraCharge" type="text" v-model="editTarget.extraCharge" class="form-control text-center w-auto d-inline-block mx-2" placeholder="z.B. 5,50" />

                            &euro; netto
                        </div>

                        <div class="divider"></div>

                        <!-- Availability -->
                        <label class="d-block">
                            Verfügbarkeit
                            <span class="required">*</span>
                        </label>

                        <!-- Always available -->
                        <div class="custom-control custom-checkbox pl-0">
                            <input type="checkbox" class="custom-control-input" id="boardTypeAlwaysAvailable" v-model="editTarget.is_always_available">
                            <label class="custom-control-label" for="boardTypeAlwaysAvailable">
                                Dauerhaft verfügbar
                            </label>
                        </div>

                        <!-- Not always available -->
                        <div class="pt-3 mt-3 border-top" v-if="! editTarget.is_always_available">
                            <label class="d-block">Wochentage</label>

                            <p>
                                <i class="fa fa-info-circle mr-1"></i>
                                Wählen Sie die Tage, an denen die Verpflegungsart zu diesem Aufpreis verfügbar ist.
                            </p>

                            <!-- Monday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_0_'" type="checkbox" v-model="editTarget.isMonday" />
                                <label :for="'board_type_weekdays_0_'">
                                    Montag
                                </label>
                            </div>

                            <!-- Tuesday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_1_'" type="checkbox" v-model="editTarget.isTuesday" />
                                <label :for="'board_type_weekdays_1_'">
                                    Dienstag
                                </label>
                            </div>

                            <!-- Wednesday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_2_'" type="checkbox" v-model="editTarget.isWednesday" />
                                <label :for="'board_type_weekdays_2_'">
                                    Mittwoch
                                </label>
                            </div>

                            <!-- Thursday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_3_'" type="checkbox" v-model="editTarget.isThursday" />
                                <label :for="'board_type_weekdays_3_'">
                                    Donnerstag
                                </label>
                            </div>

                            <!-- Friday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_4_'" type="checkbox" v-model="editTarget.isFriday" />
                                <label :for="'board_type_weekdays_4_'">
                                    Freitag
                                </label>
                            </div>

                            <!-- Saturday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_5_'" type="checkbox" v-model="editTarget.isSaturday" />
                                <label :for="'board_type_weekdays_5_'">
                                    Samstag
                                </label>
                            </div>

                            <!-- Sunday -->
                            <div class="form-group mr-2">
                                <input :id="'board_type_weekdays_6_'" type="checkbox" v-model="editTarget.isSunday" />
                                <label :for="'board_type_weekdays_6_'">
                                    Sonntag
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Edit additional service -->
                    <div class="edit-board-type" v-if="currentEdit === 'additionalService'">
                        <h2>
                            Extra bearbeiten
                        </h2>
                        <p class="lead">
                            Bitte geben Sie alle erforderlichen Informationen an.
                        </p>

                        <!-- Progress bar -->
                        <div class="progress mb-4">
                            <div class="progress-bar font-weight-900" :class="{ 'bg-success': additionalServiceProgress(editTarget) === 100 }" role="progressbar" :style="{ width: additionalServiceProgress(editTarget) + '%' }" :aria-valuenow="additionalServiceProgress(editTarget)" aria-valuemin="0" aria-valuemax="100">
                                {{ additionalServiceProgress(editTarget) }}
                                %
                            </div>
                        </div>

                        <!-- Name -->
                        <div class="form-group">
                            <label for="name">
                                Name
                                <span class="required">*</span>
                            </label>

                            <input type="text" v-model="editTarget.name" class="form-control" id="additionalServiceName" placeholder="Name für dieses Extra eingeben" />
                            <small class="d-block text-muted mt-1">
                                Beispiele: <strong>Ganzkörper-Massage</strong> oder <strong>Romantisches Abendessen</strong>
                            </small>
                        </div>

                        <!-- Subtitle -->
                        <div class="form-group">
                            <label for="name">
                                Untertitel
                                <span class="required">*</span>
                            </label>

                            <input type="text" v-model="editTarget.subtitle" class="form-control" placeholder="Untertitel eingeben" />
                            <small class="d-block text-muted mt-1">
                                Beispiele: <strong>60 Minuten lang entspannen</strong> oder <strong>3-Gänge inkl. Flasche Wein</strong>
                            </small>
                        </div>

                        <div class="divider"></div>

                        <!-- Description -->
                        <div class="form-group">
                            <label for="description">
                                Kurzbeschreibung
                                <span class="required">*</span>
                                <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Beschreiben Sie dieses Extra etwas ausführlicher indem Sie auf einzelne Leistungen eingehen, welcher der Gast mit diesem Extra erhält."></i>
                            </label>

                            <textarea rows="3" v-model="editTarget.description" id="description" class="form-control" placeholder="Geben Sie eine Beschreibung ein." />
                        </div>

                        <!-- Add image -->
                        <div class="custom-file">
                            <input type="file" accept="image/*" class="custom-file-input" id="additionalServiceImage" @change="onAdditionalServiceImageChange">
                            <label class="custom-file-label btn btn-link text-left text-primary" for="additionalServiceImage" style="font-size: 14px;">
                                <i class="fa fa-plus mr-1"></i>
                                Bild hinzufügen
                            </label>
                        </div>

                        <!-- Images -->
                        <div v-if="editTarget.image">
                            <div class="card border d-inline-block mr-2 mt-2" style="width: 140px; max-height: 140px; vertical-align: top;">
                                <!-- Remove image -->
                                <button type="button" class="btn btn-link text-white" style="position: absolute; right: 0; top: 0;" @click="removeAdditionalServiceImage">
                                    <i class="fa fa-trash"></i>
                                </button>

                                <img :src="editTarget.image.url" class="card-img-top" />
                            </div>
                        </div>

                        <div class="divider"></div>

                        <!-- Amount type -->
                        <label class="d-block">
                            Buchbarkeit
                            <span class="required">*</span>
                        </label>

                        <div class="form-group w-100">
                            <div class="btn-group btn-group-toggle w-100">
                                <!-- Per person -->
                                <label class="btn btn-outline-primary btn-lg px-3" for="amount_type_person" :class="{ active: editTarget.amountType === 'person' }">
                                    <input type="radio" value="person" id="amount_type_person" autocomplete="off" v-model="editTarget.amountType">
                                    Pro Person
                                </label>

                                <!-- Per booking -->
                                <label class="btn btn-outline-primary btn-lg px-3" for="amount_type_booking" :class="{ active: editTarget.amountType === 'booking' }">
                                    <input type="radio" value="booking" id="amount_type_booking" autocomplete="off" v-model="editTarget.amountType">
                                    Pro Buchung
                                </label>
                            </div>
                        </div>

                        <div class="divider"></div>

                        <!-- Price -->
                        <label class="d-block">
                            Preis
                            <span class="required">*</span>
                            <i class="fa fa-info-circle ml-1" data-toggle="tooltip" title="Bitte geben Sie den netto-Preis an, welcher pro Person oder pro Buchung berechnet werden soll."></i>
                        </label>

                        <div class="vertical-align-middle">
                            <label for="additionalServicePrice">
                                Preis
                            </label>

                            <input id="additionalServicePrice" type="text" v-model="editTarget.price" class="form-control text-center w-auto d-inline-block mx-2" placeholder="z.B. 7" />

                            &euro; netto
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Success -->
        <div class="container success-page pb-5" v-if="isSuccess">
            <img class="img-fluid d-block mx-auto mt-5" style="max-height: 400px;" :src="$cdn.default + 'illustrations/easy.svg?raw=true'" />

            <h2 class="text-center mt-5">
                Vielen Dank für Ihre Anfrage!
            </h2>

            <p class="lead text-center">
                Unser Team wird Ihre Kooperationsanfrage in Kürze prüfen und sich mit Ihenn in Verbindung setzen.
            </p>

            <div class="text-center mt-5">
                <a :href="$window.route('frontend.partners.hotels.index')" class="btn btn-primary btn-lg px-5">
                    Fertig
                </a>
            </div>
        </div>
    </div>
</template>

<script>
    import IBAN from 'iban'
    import DateRangePicker from 'vue2-daterange-picker'
    import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

    export default {
        props: ['googleKey'],
        components: {
            DateRangePicker,
        },
        data() {
            return {
                datePickerLocaleData: {
                    direction: 'ltr',
                    format: 'dd.mm.yyyy',
                    separator: ' - ',
                    applyLabel: 'Übernehmen',
                    cancelLabel: 'Abbrechen',
                    weekLabel: 'W',
                    customRangeLabel: 'Zeitraum wählen',
                    daysOfWeek: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
                    monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
                    firstDay: 0
                },

                // Steps
                steps: ['productTypes', 'details', 'connections', 'summary'],
                currentStep: 'productTypes',
                showDropdown: null,

                // Details
                hotelQuery: '',
                isLoadingHotels: false,
                isLoadingHotelsError: false,
                hotels: [],
                isGoogleLoaded: false,
                isManualHotel: false,
                manualHotelPlaceId: null,
                manualHotelName: '',
                manualHotelStreetHouseNumber: '',
                manualHotelZipcode: '',
                manualHotelCity: '',
                manualHotelCountry: 'de',
                hotelSelectableCountries: [
                    'de', 'at', 'ch', 'nl', 'fr', 'es', 'pl', 'lu', 'ru', 'dk', 'it', 'pt', 'cz', 'be'
                ],

                // Edit
                currentEdit: null,
                editTarget: null,

                // Product types
                possibleProductTypes: [{
                    type: 'arrangement',
                    title: 'Arrangement',
                    subtitle: 'Ich möchte eine Übernachtung mit Inklusivleistungen paketieren.',
                    icon: 'fa fa-box-full',
                    badge: 'Empfohlen',
                    badgeColor: 'primary',
                    info: 'Direkt buchbar',
                    infoIcon: 'fa fa-bolt mr-1',
                },
                {
                    type: 'flash_sale',
                    title: 'Flash-Sale',
                    subtitle: 'Ich möchte in kurzer Zeit einen exklusiven Preis anbieten.',
                    icon: 'fa fa-rocket',
                    badge: 'Umsatz-Boost',
                    badgeColor: 'sale',
                    info: 'Direkt buchbar',
                    infoIcon: 'fa fa-bolt mr-1',
                },{
                    type: 'package',
                    title: 'Erlebnispaket mit Aktivität',
                    subtitle: 'Ich möchte mit externen Aktivitäten paketiert werden.',
                    icon: 'fa fa-cubes',
                    info: 'Prüfung durch unsere Redaktion',
                },
                {
                    type: 'hotel_only',
                    title: 'Hotel-Only',
                    subtitle: 'Ich möchte ein reines Übernachtungsangebot erstellen.',
                    icon: 'fa fa-bed',
                    info: 'Direkt buchbar',
                    infoIcon: 'fa fa-bolt mr-1',
                },
                {
                    type: 'voucher',
                    title: 'Gutschein',
                    subtitle: 'Ich möchte flexibel meine Auslastung steuern.',
                    icon: 'fa fa-ticket',
                    info: 'Auf Anfrage',
                }],

                // Connections
                channelManagerQuery: '',
                possibleChannelManagers: [],
                maxChannelManagers: 5,
                isShowAddCustomChannelManager: false,
                customChannelManagerName: '',
                channelManagers: [],

                // Components
                possibleBoardTypes: [],
                possibleRoomTypes: [],
                newIncludedService: '',

                // Notification
                possibleNotificationTypes: [{
                    type: 'single',
                    title: 'E-Mail',
                    subtitle: 'Ich möchte pro Buchung eine E-Mail erhalten.',
                    icon: 'fa fa-envelope-open-text',
                    badge: 'Empfohlen',
                    badgeColor: 'primary',
                },
                {
                    type: 'bookingList',
                    title: 'Buchungslisten',
                    subtitle: 'Wir senden Ihnen wöchentlich per E-Mail eine Buchungsliste mit Anreisen der Folgewoche.',
                    icon: 'fa fa-stream',
                }],

                // Billing
                possibleBillingTypes: [{
                    type: 'payout',
                    title: 'Gutschrift',
                    subtitle: 'Monatlich; immer zum 5. des laufenden Monats werden alle Anreisen des Vormonats gutgeschrieben.',
                    icon: 'fa fa-receipt',
                    badge: 'Empfohlen',
                    badgeColor: 'primary',
                },
                {
                    type: 'invoice',
                    title: 'Rechnung',
                    subtitle: 'Sie stellen uns wöchentlich oder monatlich, nach Abreise der Gäste, eine Rechnung.',
                    icon: 'fa fa-file-invoice',
                }],

                // Flash sale
                possibleFlashSaleTypes: [{
                    type: 'period',
                    title: 'Begrenzter Zeitraum',
                    subtitle: 'Ihr Produkt wird ausschließlich innerhalb des genannten Zeitraumes online sein.',
                    icon: 'fa fa-hourglass-half',
                },
                {
                    type: 'endless',
                    title: 'Dauerhaft verkaufen',
                    subtitle: 'Das Produkt bleibt dauerthaft online, solange Verfügbarkeiten vorhanden sind.',
                    icon: 'fa fa-infinity',
                }],
                possibleFlashSaleTargets: [{
                    type: 'public',
                    title: 'Öffentliche Bewerbung',
                    subtitle: 'Das Produkt und der Preis erscheinen öffentlich auf unserer Webseite.',
                    icon: 'fa fa-users-crown',
                },
                {
                    type: 'exklusive',
                    title: 'Verdeckte Bewerbung',
                    subtitle: 'Das Produkt wird innerhalb eines geschlossenen Verteilerkreises beworben. Der reduzierte Preis wird nicht öffentlich zugänglich gemacht.',
                    icon: 'fa fa-user-secret',
                }],

                // Request data
                hotel: null,
                productTypes: [],
                rooms: [],
                boardTypes: [],
                additionalServices: [],
                notification: {
                    types: ['single'], // single || bookingList
                    email: null,
                },
                billing: {
                    type: 'payout', // payout || invoice
                    isSameEmail: true,
                    email: null,
                    iban: null,
                },
                flashSale: {
                    type: 'period', // period || endless
                    from: null,
                    to: null,
                    target: 'public', // public || exklusive
                },
                voucherRequest: {
                    redemptionType: 'availability', // availability || request
                    validity: 'oneYear', // oneYear || lessThanAYear || moreThanAYear
                    existingVoucher: false,
                    existingVoucherUrl: null,
                },
                contactPerson: {
                    fullname: null,
                    email: null,
                    phone: null,
                },
                message: '',
                isAcceptDataProtection: false,

                // Submit
                isLoading: false,
                errors: {},
                isSuccess: false,
            }
        },
        mounted() {
            var self = this;

            // Listen on browser back button
            history.pushState(null, null, window.location.pathname)
            window.onpopstate = function(event) {
                event.preventDefault()
                self.prev()
            };

            // Include Google SDK
            let stripeScript = document.createElement('script')
            stripeScript.onload = this.initGoogle
            stripeScript.setAttribute('src', 'https://maps.googleapis.com/maps/api/js?key=' + this.googleKey + '&libraries=places')
            document.head.appendChild(stripeScript)

            // Autofocus
            setTimeout(() => {
                $('div.hotel-query-input input').focus()
            }, 1000)

            // Preload possible board types
            axios.get(route('api.frontend.partners.hotels.boardTypes'), {}).then((response) => {
                self.possibleBoardTypes = response.data.data
            }).catch(function (error) {
                console.log(error);
            });

            // Preload possible room types
            axios.get(route('api.frontend.partners.hotels.roomTypes'), {}).then((response) => {
                self.possibleRoomTypes = response.data.data
            }).catch(function (error) {
                console.log(error);
            });

            // Preload possible channel managers
            axios.get(route('api.frontend.partners.hotels.channelManagers'), {}).then((response) => {
                self.possibleChannelManagers = response.data.data
            }).catch(function (error) {
                console.log(error);
            });
        },
        computed: {
            validManualHotel() {
                return this.hotelSelectableCountries.indexOf(this.manualHotelCountry) !== -1 &&
                this.manualHotelName !== '' &&
                this.manualHotelStreetHouseNumber !== '' &&
                this.manualHotelZipcode !== '' &&
                this.manualHotelCity !== '' &&
                this.manualHotelCountry !== ''
            },
            validComponents() {
                var self = this

                // Rooms
                let invalidRooms = this.rooms.filter(room => {
                    return ! (
                        self.validRoomInfo(room) &&
                        self.validBoardType(room) &&
                        self.validRoomOccupancy(room) &&
                        self.validRoomAvailabilities(room) &&
                        self.validRoomCancellationRules(room)
                        )
                })

                // Board types
                let invalidBoardTypes = this.boardTypes.filter(boardType => {
                    return ! (
                        self.validBoardTypeInfo(boardType) &&
                        self.validBoardTypeAvailability(boardType) &&
                        self.validBoardTypeExtraCharge(boardType)
                        )
                })

                // Additional services
                let invalidAdditionalServices = this.additionalServices.filter(additionalService => {
                    return ! (
                        self.validAdditionalServiceInfo(additionalService) &&
                        self.validAdditionalServiceDescription(additionalService) &&
                        self.validAdditionalServicePrice(additionalService)
                        )
                })

                return this.rooms.length > 0 && invalidRooms.length === 0 && invalidBoardTypes.length === 0 && invalidAdditionalServices.length === 0
            },
            validNotification() {
                const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

                return re.test(String(this.notification.email).toLowerCase()) && this.notification.types.indexOf('single') !== -1
            },
            validBilling() {
                // Validate email
                const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                let validEmail = this.billing.isSameEmail || (! this.billing.isSameEmail && re.test(String(this.billing.email).toLowerCase()))

                // Iban
                let validIban = this.billing.type === 'invoice' || (this.billing.type === 'payout' && IBAN.isValid(this.billing.iban))

                return validEmail && validIban
            },
            validFlashSale() {
                return this.flashSale.type === 'endless' || (this.flashSale.type === 'period' && this.flashSale.from !== null && this.flashSale.to !== null)
            },

            filteredChannelManagers() {
                // Reset max
                this.maxChannelManagers = 5

                const searchQuery = this.channelManagerQuery
                return this.possibleChannelManagers.filter((channelManager) => channelManager.name.toLowerCase().trim().includes(searchQuery.toLowerCase().trim()))
            },
        },
        methods: {
            scrollTo(target) {
                $('html,body').stop().animate({
                    scrollTop: $(target).offset().top - 20
                }, 500, 'swing');
            },

            // Toggle dropdown
            toggleDropdown(dropdownName) {
                if (this.showDropdown === dropdownName) {
                    this.showDropdown = null;
                } else {
                    this.cancelAll();
                    this.showDropdown = dropdownName;
                }
            },

            // Cancel
            cancelAll() {
                this.showDropdown = null;
            },

            initGoogle() {
                this.isGoogleLoaded = true
            },

            // Next + prev
            next() {
                let index = this.steps.indexOf(this.currentStep)

                if (index < this.steps.length) {
                    this.currentStep = this.steps[index + 1]
                    history.pushState(null, null, window.location.pathname)
                }
            },
            prev() {
                let index = this.steps.indexOf(this.currentStep)

                if (index > 0) {
                    this.currentStep = this.steps[index - 1]
                } else {
                    window.history.back()
                }
            },

            // Search hotels
            searchHotels() {
                var self = this;

                if (this.hotelQuery.length <= 2) {
                    this.isLoadingHotels = false
                    return;
                }

                this.isLoadingHotels = true;

                if (this.isGoogleLoaded) {
                    var service = new google.maps.places.AutocompleteService();
                    service.getPlacePredictions({
                        input: this.hotelQuery,
                    }, (predictions, status) => {
                        if (status === google.maps.places.PlacesServiceStatus.OK) {
                            self.isLoadingHotelsError = false

                            self.hotels = predictions.map(item => {
                                return {
                                    id: item.id,
                                    place_id: item.place_id,
                                    name: item.structured_formatting.main_text,
                                    subname: item.structured_formatting.secondary_text,
                                    // street_house_number: this.manualHotelStreetHouseNumber,
                                    // zipcode: this.manualHotelZipcode,
                                    // city: this.manualHotelCity,
                                    // country: this.manualHotelCountry,
                                }
                            })
                        } else {
                            self.isLoadingHotelsError = true
                        }

                        self.isLoadingHotels = false
                    });
                }
            },
            selectHotel(hotel) {
                var self = this

                var service = new google.maps.places.PlacesService(
                    document.createElement('div')
                    );
                service.getDetails({
                    placeId: hotel.place_id,
                    fields: ['address_component', 'geometry', 'formatted_address']
                }, (details, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK) {

                        // Filter address parts
                        const streetComponents = details.address_components.filter((component) => component.types.indexOf('route') !== -1);
                        const houseNumberComponents = details.address_components.filter((component) => component.types.indexOf('street_number') !== -1);
                        const cityComponents = details.address_components.filter((component) => component.types.indexOf('locality') !== -1);
                        const zipcodeComponents = details.address_components.filter((component) => component.types.indexOf('postal_code') !== -1);
                        const countryComponents = details.address_components.filter((component) => component.types.indexOf('country') !== -1);

                        // Street house number
                        const streetHouseNumber = (streetComponents.length > 0 ? streetComponents[0].long_name : '') + ' ' + (houseNumberComponents.length > 0 ? houseNumberComponents[0].long_name : '')

                        // Reset results + set hotel
                        self.hotels = []
                        self.hotel = {
                            place_id: hotel.place_id,
                            name: hotel.name,
                            subname: details.formatted_address,
                            street_house_number: streetHouseNumber,
                            zipcode: zipcodeComponents.length > 0 ? zipcodeComponents[0].long_name : '',
                            city: cityComponents.length > 0 ? cityComponents[0].long_name : '',
                            country: countryComponents.length > 0 ? countryComponents[0].short_name.toLowerCase() : '',
                        }
                    }
                });
            },
            editHotel() {
                this.currentStep = 'details'

                // Set manual
                this.manualHotelPlaceId = this.hotel.place_id
                this.manualHotelName = this.hotel.name
                this.manualHotelStreetHouseNumber = this.hotel.street_house_number
                this.manualHotelZipcode = this.hotel.zipcode
                this.manualHotelCity = this.hotel.city
                this.manualHotelCountry = this.hotel.country
                this.isManualHotel = true

                // Reset
                this.hotel = null
                this.hotelQuery = ''

                // Autofocus
                setTimeout(() => {
                    $('div.hotel-query-input input').focus()
                }, 300)
            },
            toggleDetails() {
                this.isManualHotel = ! this.isManualHotel

                if (this.isManualHotel) {
                    this.manualHotelPlaceId = null
                }
            },
            useHotel() {
                let country = this.$window.Lang.get('checkout.steps.confirmation.countries.' + this.manualHotelCountry)

                this.hotel = {
                    place_id: this.manualHotelPlaceId,
                    name: this.manualHotelName,
                    subname: `${this.manualHotelStreetHouseNumber}, ${this.manualHotelZipcode} ${this.manualHotelCity}, ${country}`,
                    street_house_number: this.manualHotelStreetHouseNumber,
                    zipcode: this.manualHotelZipcode,
                    city: this.manualHotelCity,
                    country: this.manualHotelCountry,
                }
            },

            // Product types
            toggleProductType(type) {
                let index = this.productTypes.indexOf(type)

                if (index === -1) {
                    this.productTypes.push(type)
                } else {
                    this.productTypes.splice(index, 1)
                }
            },

            // Channel managers
            toggleChannelManager(channelManager) {
                let index = this.$lodash.findIndex(this.channelManagers, { name: channelManager.name })

                if (index === -1) {
                    this.channelManagers.push(channelManager)
                } else {
                    this.channelManagers.splice(index, 1)
                }
            },
            addCustomChannelManager() {
                if (this.customChannelManagerName.trim().length > 0) {
                    this.channelManagers.push({
                        id: null,
                        name: this.customChannelManagerName
                    })
                }
                this.customChannelManagerName = ''

                this.next()
            },

            // Save edit
            saveEdit() {
                // Room
                if (this.currentEdit === 'room') {
                    let index = this.$lodash.findIndex(this.rooms, {
                        id: this.editTarget.id,
                    })

                    if (index !== -1) {
                        this.rooms.splice(index, 1, this.editTarget)
                    }
                }

                // Board type
                if (this.currentEdit === 'boardType') {
                    let index = this.$lodash.findIndex(this.boardTypes, {
                        id: this.editTarget.id,
                    })

                    if (index !== -1) {
                        this.boardTypes.splice(index, 1, this.editTarget)
                    }
                }

                // Additional service
                if (this.currentEdit === 'additionalService') {
                    let index = this.$lodash.findIndex(this.additionalServices, {
                        id: this.editTarget.id,
                    })

                    if (index !== -1) {
                        this.additionalServices.splice(index, 1, this.editTarget)
                    }
                }

                // Cleanup
                this.currentEdit = null
                this.editTarget = null
            },

            editComponents() {
                this.currentStep = 'components'
            },

            // Room image change
            onRoomImageChange(event) {
                var self = this

                for (var i = 0; i < event.target.files.length; i++) {
                    self.editTarget.images.push({
                        url: URL.createObjectURL(event.target.files[i]),
                        data: event.target.files[i],
                    })
                }
            },

            // Remove room image
            removeRoomImage(index) {
                this.editTarget.images.splice(index, 1)
            },

            // Add included service
            addIncludedService() {
                this.editTarget.includedServices.push({
                    name: this.newIncludedService,
                })

                // Reset input
                this.newIncludedService = ''

                // Focus input
                $('input#includedService').focus()
            },

            // Remove included service
            removeIncludedService(index) {
                this.editTarget.splice(index, 1)
            },

            // Add availability
            addAvailability() {
                var self = this;

                this.editTarget.availabilities.push({
                    dateRange: {
                        startDate: null,
                        endDate: null,
                    },
                    from: null,
                    to: null,
                    amount: 0,
                    release: 0,
                    price: 0,
                    minNights: 1,
                    singlePersonExtraCharge: 100,

                    // Weekdays
                    isMonday: true,
                    isTuesday: true,
                    isWednesday: true,
                    isThursday: true,
                    isFriday: true,
                    isSaturday: true,
                    isSunday: true,
                })
            },

            // Remove availability
            removeAvailability(index) {
                this.editTarget.availabilities.splice(index, 1)
            },

            // Remove stop sale
            removeStopSale(index) {
                this.editTarget.stopSales.splice(index, 1)
            },

            // Add additional person extra charge
            addAdditionalPersonExtraCharge() {
                this.editTarget.additionalPersonsExtraCharges.push({
                    type: 'adult',
                    from: 0,
                    to: 17,
                    value: 0,
                    valueType: 'euro',
                })
            },

            // Remove additional person extra charge
            removeAdditionalPersonsExtraCharge(index) {
                this.editTarget.additionalPersonsExtraCharges.splice(index, 1)
            },

            // Add cancellation rule
            addCancellationRule() {
                this.editTarget.cancellationRules.push({
                    daysBefore: 0,
                    valueType: 'percent',
                    value: 100,
                })
            },

            // Remove cancellation rule
            removeCancellationRule(index) {
                this.editTarget.cancellationRules.splice(index, 1)
            },

            // Rooms
            // Add room
            addRoom(type) {
                // Default board type
                let defaultBoardType = null

                if (this.$lodash.findIndex(this.possibleBoardTypes, { name: 'Nur Übernachtung' }) !== -1) {
                    defaultBoardType = this.$lodash.find(this.possibleBoardTypes, { name: 'Nur Übernachtung' })
                }


                // Individual
                if (type === null) {
                    type = {
                        focus: true,
                        name: 'Eigenes Zimmer',
                        description: '',
                        min_adults: 0,
                        max_adults: 0,
                        min_children: 0,
                        max_children: 0,
                        min_persons: 0,
                        max_persons: 0,
                        standard_capacity: null,
                    }
                }

                // Using template
                this.rooms.push({
                    id: Math.random().toString(36).substr(2, 9),
                    name: type.name,
                    description: type.description,
                    boardType: defaultBoardType,
                    images: [],
                    occupancy: {
                        minAdults: type.min_adults,
                        maxAdults: type.max_adults,
                        minChildren: type.min_children,
                        maxChildren: type.min_children,
                        minPersons: type.min_persons,
                        maxPersons: type.min_persons,

                        // Additional persons
                        maxAdditionalAdults: 0,
                        maxAdditionalChildren: 0,
                        maxAdditionalPersons: 0,
                    },
                    isAdditionalPersons: false,
                    additionalPersonsExtraCharges: [],
                    standardCapacity: type.standard_capacity,
                    availabilities: [],
                    cancellationRules: [],
                    stopSales: [],
                    includedServices: [],
                })

                this.cancelAll()

                // Set new room as current edit
                this.editRoom(this.rooms[this.rooms.length - 1])

                // Focus
                if (type.focus) {
                    setTimeout(() => {
                        $('input#roomName').focus()
                    }, 500)

                    setTimeout(() => {
                        $('input#roomName').select()
                    }, 600)
                }
            },

            // Remove room
            removeRoom(room) {
                let index = this.$lodash.findIndex(this.rooms, {
                    id: room.id,
                })

                if (confirm('Möchten Sie das Zimmer wirklich entfernen?')) {
                    if (index !== -1) {
                        this.rooms.splice(index, 1)
                    }
                }
            },

            // Toggle room additional persons
            toggleRoomAdditionalPersons(room) {
                // Find room
                let index = this.$lodash.findIndex(this.rooms, { id: room.id })

                // Update room in rooms list
                this.rooms[index].isAdditionalPersons = ! this.rooms[index].isAdditionalPersons

                // Reset additional persons if disabled
                if (! this.rooms[index].isAdditionalPersons) {
                    // Additional persons
                    this.rooms[index].maxAdditionalAdults = null
                    this.rooms[index].maxAdditionalChildren = null
                    this.rooms[index].maxAdditionalPersons = null
                }

                // Set default additional persons if enabled
                if (! this.rooms[index].isAdditionalPersons) {
                    // Additional persons
                    this.rooms[index].maxAdditionalAdults = 1
                    this.rooms[index].maxAdditionalChildren = 1
                    this.rooms[index].maxAdditionalPersons = 1
                }
            },

            // Edit room
            editRoom(room) {
                this.currentStep = 'components'

                var self = this

                this.currentEdit = 'room'
                this.editTarget = room
            },

            // Validate room
            validRoomInfo(room) {
                // Name
                if (room.name.length <= 3) {
                    return false
                }

                return true
            },
            validBoardType(room) {
                // Board type
                if (room.boardType === null) {
                    return false
                }

                return true
            },
            validRoomOccupancy(room) {
                // Occupancy
                if (room.occupancy.maxPersons < 1) {
                    return false
                }
                if (room.occupancy.minAdults < 1) {
                    return false
                }

                return true
            },
            validRoomAvailabilities(room) {
                if (room.availabilities.length === 0) {
                    return false
                }

                let invalidAvailabilities = room.availabilities.filter(availability => {
                    if (availability.dateRange.startDate === null) {
                        return true
                    }

                    if (availability.dateRange.endDate === null) {
                        return true
                    }

                    if (! (availability.minNights > 0)) {
                        return true
                    }

                    if (availability.amount <= 0) {
                        return true
                    }

                    // Price
                    let price = availability.price
                    if (isNaN(price)) {
                        price = parseFloat(price.replace(',', '.'), 10)
                    }
                    if (price <= 0) {
                        return true
                    }

                    // Check if all weekdays are disabled
                    if (
                        ! availability.isMonday &&
                        ! availability.isTuesday &&
                        ! availability.isWednesday &&
                        ! availability.isThursday &&
                        ! availability.isFriday &&
                        ! availability.isSaturday &&
                        ! availability.isSunday)
                    {
                        return true
                    }

                    return false
                })

                return true && invalidAvailabilities.length === 0
            },
            validRoomCancellationRules(room) {
                if (room.cancellationRules.length === 0) {
                    return false
                }

                let invalidCancellationRules = room.cancellationRules.filter(cancellationRule => {
                    if (cancellationRule.daysBefore < 0) {
                        return true
                    }

                    // Price
                    let price = cancellationRule.value
                    if (isNaN(price)) {
                        price = parseFloat(price.replace(',', '.'), 10)
                    }
                    if (price < 0) {
                        return true
                    }

                    return false
                })

                return true && invalidCancellationRules.length === 0
            },
            validRoomStopSales(room) {
                if (room.stopSales.length === 0) {
                    return false
                }

                return true
            },
            validRoomIncludedServices(room) {
                let invalidIncludedServices = room.includedServices.filter(item => {
                    if (item.name.length < 2) {
                        return true
                    }

                    return false
                })

                return invalidIncludedServices.length === 0 && this.editTarget.includedServices.length > 0
            },

            // Room progress
            roomProgress(room) {
                let progress = 0

                progress += this.validRoomInfo(room) ? 20 : 0
                progress += this.validBoardType(room) ? 20 : 0
                progress += this.validRoomOccupancy(room) ? 20 : 0
                progress += this.validRoomAvailabilities(room) ? 20 : 0
                progress += this.validRoomCancellationRules(room) ? 20 : 0

                return progress
            },

            // Board types
            // Add board type
            addBoardType(boardType) {
                // Individual
                if (boardType === null) {
                    boardType = {
                        focus: true,
                        id: null,
                        name: 'Unbekannt',
                        code: null,
                        description: null,
                    }
                }
                this.boardTypes.push({
                    id: Math.random().toString(36).substr(2, 9),
                    type_id: boardType.id,
                    name: boardType.name,
                    code: boardType.code,
                    extraCharge: 0,
                    description: boardType.description,
                    is_always_available: true,

                    // Weekdays
                    isMonday: true,
                    isTuesday: true,
                    isWednesday: true,
                    isThursday: true,
                    isFriday: true,
                    isSaturday: true,
                    isSunday: true,
                })

                this.cancelAll()

                // Set new board type as current edit
                this.editBoardType(this.boardTypes[this.boardTypes.length - 1])

                // Focus
                if (boardType.focus) {
                    setTimeout(() => {
                        $('input#boardTypeName').focus()
                    }, 500)

                    setTimeout(() => {
                        $('input#boardTypeName').select()
                    }, 600)
                }
            },

            // Edit board type
            editBoardType(boardType) {
                this.currentStep = 'components'

                this.currentEdit = 'boardType'
                this.editTarget = boardType
            },

            // Remove board type
            removeBoardType(boardType) {
                let index = this.$lodash.findIndex(this.boardTypes, {
                    id: boardType.id,
                })

                if (confirm('Möchten Sie die Verpflegungsart wirklich entfernen?')) {
                    if (index !== -1) {
                        this.boardTypes.splice(index, 1)
                    }
                }
            },

            // Validate board type
            validBoardTypeInfo(boardType) {
                // Name
                if (boardType.name.length <= 3) {
                    return false
                }

                return true
            },
            validBoardTypeAvailability(boardType) {
                // Check if all weekdays are disabled
                if (
                    ! boardType.isMonday &&
                    ! boardType.isTuesday &&
                    ! boardType.isWednesday &&
                    ! boardType.isThursday &&
                    ! boardType.isFriday &&
                    ! boardType.isSaturday &&
                    ! boardType.isSunday)
                {
                    return false
                }

                return true;
            },
            validBoardTypeExtraCharge(boardType) {
                let extraCharge = boardType.extraCharge
                if (isNaN(extraCharge)) {
                    extraCharge = parseFloat(extraCharge.replace(',', '.'), 10)
                }

                if (! (extraCharge > 0)) {
                    return false
                }

                return true
            },

            // Board type progress
            boardTypeProgress(boardType) {
                let progress = 0

                progress += this.validBoardTypeInfo(boardType) ? 33 : 0
                progress += this.validBoardTypeAvailability(boardType) ? 33 : 0
                progress += this.validBoardTypeExtraCharge(boardType) ? 34 : 0

                return progress
            },

            // Weekdays list
            weekdaysList(target) {
                let list = []

                // Monday
                if (target.isMonday) {
                    list.push('Montag')
                }

                // Tuesday
                if (target.isTuesday) {
                    list.push('Dienstag')
                }

                // Wednesday
                if (target.isWednesday) {
                    list.push('Mittwoch')
                }

                // Thursday
                if (target.isThursday) {
                    list.push('Donnerstag')
                }

                // Friday
                if (target.isFriday) {
                    list.push('Freitag')
                }

                // Saturday
                if (target.isSaturday) {
                    list.push('Samstag')
                }

                // Sunday
                if (target.isSunday) {
                    list.push('Sonntag')
                }

                return list.join(', ')
            },

            // Additional services
            // Add additional service
            addAdditionalService() {
                this.additionalServices.push({
                    focus: true,
                    id: Math.random().toString(36).substr(2, 9),
                    name: 'Unbekannt',
                    subtitle: null,
                    description: null,
                    image: null,
                    price: 0,
                    amountType: 'person', // person || booking
                })

                this.cancelAll()

                // Set new additional service as current edit
                this.editAdditionalService(this.additionalServices[this.additionalServices.length - 1])

                // Focus
                setTimeout(() => {
                    $('input#additionalServiceName').focus()
                }, 500)

                setTimeout(() => {
                    $('input#additionalServiceName').select()
                }, 600)
            },

            // Remove additional service
            removeAdditionalService(additionalService) {
                let index = this.$lodash.findIndex(this.additionalServices, {
                    id: additionalService.id,
                })

                if (confirm('Möchten Sie dieses Extra wirklich entfernen?')) {
                    if (index !== -1) {
                        this.additionalServices.splice(index, 1)
                    }
                }
            },

            // Edit additional service
            editAdditionalService(additionalService) {
                this.currentStep = 'components'

                this.currentEdit = 'additionalService'
                this.editTarget = additionalService
            },

            // Additional service image change
            onAdditionalServiceImageChange(e) {
                if (e.target.files.length > 0) {
                    this.editTarget.image = {
                        url: URL.createObjectURL(e.target.files[0]),
                        data: e.target.files[0],
                    }
                }
            },

            // Remove additional service image
            removeAdditionalServiceImage() {
                this.editTarget.image = null
            },

            // Validate additional service
            validAdditionalServiceInfo(additionalService) {
                // Name
                if (! additionalService.name) {
                    return false
                }
                if (additionalService.name && additionalService.name.length <= 3) {
                    return false
                }

                // Subtitle
                if (! additionalService.subtitle) {
                    return false
                }
                if (additionalService.subtitle.length <= 3) {
                    return false
                }

                return true
            },
            validAdditionalServiceDescription(additionalService) {
                if (! additionalService.description) {
                    return false
                }

                if (additionalService.description.length <= 50) {
                    return false
                }

                return true
            },
            validAdditionalServicePrice(additionalService) {
                let price = additionalService.price
                if (isNaN(price)) {
                    price = parseFloat(price.replace(',', '.'), 10)
                }
                if (price <= 0) {
                    return false
                }

                return true
            },

            // Additional service progress
            additionalServiceProgress(additionalService) {
                let progress = 0

                progress += this.validAdditionalServiceInfo(additionalService) ? 33 : 0
                progress += this.validAdditionalServiceDescription(additionalService) ? 33 : 0
                progress += this.validAdditionalServicePrice(additionalService) ? 34 : 0

                return progress
            },

            // Flash sale
            // Set flash sale type
            setFlashSaleType(type) {
                this.flashSale.type = type

                if (type === 'period') {
                    this.refreshFlashSale()
                }
            },

            // Set flash sale target
            setFlashSaleTarget(type) {
                this.flashSale.target = type
            },

            // Refresh flash sale
            refreshFlashSale() {
                var self = this

                // Wait for render process
                setTimeout(() => {
                    // Enable date picker
                    $('input#flashSalePeriodPicker').dateRangePicker({
                        separator: ' to ',
                        inline: true,
                        container: $('div#flashSaleCalendar'),
                        alwaysOpen: true,
                        stickyMonths: true,
                        language: 'de',
                        startOfWeek: 'monday',
                        showTopbar: false,
                        customArrowPrevSymbol: '<button type="button" class="btn"><i class="fa fa-chevron-left"></i></button>',
                        customArrowNextSymbol: '<button type="button" class="btn"><i class="fa fa-chevron-right"></i></button>'
                    }).bind('datepicker-change', function(event, data) {
                        self.flashSale.from = moment(data.date1)
                        self.flashSale.to = moment(data.date2)

                        self.cancelAll()
                    });
                }, 200)
            },

            // Edit notification
            editNotification() {
                this.currentStep = 'notification'
            },

            // Toggle notificaiton type
            toggleNotificationType(type) {
                let index = this.notification.types.indexOf(type)

                if (index === -1) {
                    this.notification.types.push(type)
                } else {
                    this.notification.types.splice(index, 1)
                }
            },

            // Edit billing
            editBilling() {
                this.currentStep = 'billing'
            },

            // Edit voucher request
            editVoucherRequest() {
                this.currentStep = 'voucher'
            },

            // Submit
            buildSubmitData() {
                let data = {
                    // Hotel
                    hotel: {
                        ...this.hotel,
                        google_place_id: this.hotel.place_id,
                        channel_managers: this.channelManagers,
                    },

                    // Product types
                    product_types: this.productTypes,

                    // Rooms
                    rooms: this.steps.indexOf('components') !== -1 ? this.rooms.map((room) => {
                        return {
                            name: room.name,
                            description: room.description,
                            board_type: room.boardType,
                            images: room.images,
                            occupancy: {
                                min_adults: room.occupancy.minAdults,
                                max_adults: room.occupancy.maxAdults,
                                min_children: room.occupancy.minChildren,
                                max_children: room.occupancy.maxChildren,
                                min_persons: room.occupancy.minPersons,
                                max_persons: room.occupancy.maxPersons,
                                max_additional_adults: room.occupancy.maxAdditionalAdults,
                                max_additional_children: room.occupancy.maxAdditionalChildren,
                                max_additional_persons: room.occupancy.maxAdditionalPersons,
                            },
                            standard_capacity: room.standardCapacity,

                            // Additional persons
                            is_additional_persons: room.isAdditionalPersons,
                            additional_persons_extra_charges: room.additionalPersonsExtraCharges.map((item) => {
                                return {
                                    type: item.type,
                                    from: item.from,
                                    to: item.to,
                                    value: String(item.value).replace(',', '.'),
                                    value_type: item.valueType,
                                }
                            }),

                            // Availabilities
                            availabilities: room.availabilities.map((item) => {
                                return {
                                    from: moment(item.dateRange.startDate).format('YYYY-MM-DD'),
                                    to: moment(item.dateRange.endDate).format('YYYY-MM-DD'),
                                    amount: item.amount,
                                    release: item.release,
                                    price: String(item.price).replace(',', '.'),
                                    min_nights: item.minNights,
                                    single_person_extra_charge: String(item.singlePersonExtraCharge).replace(',', '.'),
                                    is_monday: item.isMonday,
                                    is_tuesday: item.isTuesday,
                                    is_wednesday: item.isWednesday,
                                    is_thursday: item.isThursday,
                                    is_friday: item.isFriday,
                                    is_saturday: item.isSaturday,
                                    is_sunday: item.isSunday,
                                }
                            }),

                            // Cancellation rules
                            cancellation_rules: room.cancellationRules.map((item) => {
                                return {
                                    days_before: item.daysBefore,
                                    value_type: item.valueType,
                                    value: String(item.value).replace(',', '.'),
                                }
                            }),

                            // Stop sales
                            stop_sales: room.stopSales.map((item) => {
                                return {
                                    from: item.from.format('YYYY-MM-DD'),
                                    to: item.to.format('YYYY-MM-DD'),
                                }
                            }),

                            // Included services
                            included_services: room.includedServices,
                        }
                    }) : null,

                    // Board types
                    board_types: this.steps.indexOf('components') !== -1 ? this.boardTypes.map((boardType) => {
                        return {
                            type_id: boardType.type_id,
                            name: boardType.name,
                            code: boardType.code,
                            extra_charge: String(boardType.extraCharge).replace(',', '.'),
                            description: boardType.description,
                            is_always_available: boardType.is_always_available,
                            is_monday: boardType.isMonday,
                            is_tuesday: boardType.isTuesday,
                            is_wednesday: boardType.isWednesday,
                            is_thursday: boardType.isThursday,
                            is_friday: boardType.isFriday,
                            is_saturday: boardType.isSaturday,
                            is_sunday: boardType.isSunday,
                        }
                    }) : null,

                    // Additional services
                    additional_services: this.steps.indexOf('components') !== -1 ? this.additionalServices.map((additionalService) => {
                        return {
                            name: additionalService.name,
                            subtitle: additionalService.subtitle,
                            description: additionalService.description,
                            image: additionalService.image,
                            price: String(additionalService.price).replace(',', '.'),
                            amount_type: additionalService.amountType,
                        }
                    }) : null,

                    // Notification
                    notification: this.steps.indexOf('notification') !== -1 ? {
                        is_booking_list: this.notification.types.indexOf('bookingList') !== -1,
                        email: this.notification.email,
                    } : null,

                    // Billing
                    billing: this.steps.indexOf('billing') !== -1 ? {
                        type: this.billing.type,
                        email: this.billing.isSameEmail ? this.notification.email : this.billing.email,
                        iban: this.billing.type === 'payout' ? this.billing.iban : null,
                    } : null,

                    // Flash sale
                    flash_sale: this.steps.indexOf('flashSale') !== -1 ? {
                        type: this.flashSale.type,
                        from: this.flashSale.type === 'period' ? this.flashSale.from.format('YYYY-MM-DD') : null,
                        to: this.flashSale.type === 'period' ? this.flashSale.to.format('YYYY-MM-DD') : null,
                        target: this.flashSale.target,
                    } : null,

                    // Voucher request
                    voucher_request: this.steps.indexOf('voucherRequest') !== -1 ? {
                        redemption_type: this.voucherRequest.redemptionType,
                        validity: this.voucherRequest.validity,
                        is_existing_voucher: this.voucherRequest.existingVoucher,
                        existing_voucher_url: this.voucherRequest.existingVoucherUrl,
                    } : null,

                    // Contact person
                    contact_person: {
                        fullname: this.contactPerson.fullname,
                        email: this.contactPerson.email,
                        phone: this.contactPerson.phone,
                    },

                    // Message
                    message: this.message,

                    // Data protection
                    is_accept_data_protection: this.isAcceptDataProtection,
                }


                // Build files
                let formData = new FormData();
                let index = 0;
                data.additional_services.forEach((item) => {
                    formData.append('files[]', item.image.data)
                    formData.append('types[]', 'additional_service')
                    formData.append('fileIndex[]', index)

                    index++;
                })
                index = 0;
                data.rooms.forEach((item) => {
                    if (item.images) {
                        item.images.forEach((image) => {
                            formData.append('files[]', image.data)
                            formData.append('types[]', 'room')
                            formData.append('fileIndex[]', index)
                        })
                    }

                    index++;
                })

                return {
                    data,
                    formData,
                }
            },
            submit() {
                var self = this;

                this.isLoading = true
                this.loadingInfo = 'Anfrage wird gespeichert...'

                // Post json data
                axios.post(route('api.frontend.partners.hotels.store'), this.buildSubmitData().data).then(function (response) {
                    self.isLoading = true
                    self.uploadImages(response.data.hotel_application_id, response.data.rooms, response.data.additional_services)

                    // Scroll to top
                    setTimeout(() => {
                        $('html,body').stop().animate({
                            scrollTop: $('div.success-page').offset().top - 20
                        }, 500, 'swing');
                    }, 200)

                }).catch(function (error) {
                    self.errors = error.response.data.errors
                    self.isLoading = false

                    // Scroll to errors
                    setTimeout(() => {
                        $('html,body').stop().animate({
                            scrollTop: $('div.form-errors').offset().top - 20
                        }, 500, 'swing');
                    }, 200)
                });
            },

            uploadImages(hotelApplicationId, rooms, additionalServices) {
                var self = this;
                this.loadingInfo = 'Bilder werden hochgeladen...'

                let options = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }

                let formData = this.buildSubmitData().formData
                formData.append('rooms', rooms.join(','))
                formData.append('additional_services', additionalServices.join(','))

                axios.post(route('api.frontend.partners.hotels.uploadImages', {
                    hotelApplication: hotelApplicationId,
                }), formData, options).then(function (response) {
                    self.isSuccess = response.data.success
                }).catch(function (error) {
                    console.log(error);
                    self.$toast.error('Error. Please check the console.');
                });
            },
        },
        watch: {
            hotelQuery() {
                this.searchHotels()
            },
            currentStep() {
                $('html,body').stop().animate({
                    scrollTop: $('div.hotel-partner-form').offset().top - 20
                }, 500, 'swing');

                // Flash sale
                if (this.currentStep === 'flashSale') {
                    this.refreshFlashSale()
                }

                // Details
                if (this.currentStep === 'details') {
                    // Autofocus
                    setTimeout(() => {
                        $('div.hotel-query-input input').focus()
                    }, 300)
                }
            },
            currentEdit() {
                var self = this

                // Scroll top
                $('html,body').stop().animate({
                    scrollTop: $('div.hotel-partner-form').offset().top - 20
                }, 500, 'swing');


                // Stop sale period picker
                if (this.currentEdit === 'room') {
                    // Wait for render process
                    setTimeout(() => {
                        // Enable date picker
                        $('input#stopSalePeriodPicker').dateRangePicker({
                            separator: ' to ',
                            inline: true,
                            container: $('div#stopSaleCalendar'),
                            alwaysOpen: true,
                            stickyMonths: true,
                            language: 'de',
                            startOfWeek: 'monday',
                            showTopbar: false,
                            customArrowPrevSymbol: '<button type="button" class="btn"><i class="fa fa-chevron-left"></i></button>',
                            customArrowNextSymbol: '<button type="button" class="btn"><i class="fa fa-chevron-right"></i></button>'
                        }).bind('datepicker-change', function(event, data) {
                            self.editTarget.stopSales.push({
                                from: moment(data.date1),
                                to: moment(data.date2),
                            })

                            // Scroll to stop sales for first item
                            if (self.editTarget.stopSales.length === 1) {
                                setTimeout(() => {
                                    $('html,body').stop().animate({
                                        scrollTop: $('div.stop-sales').offset().top - 20
                                    }, 500, 'swing');
                                }, 500)
                            }

                            self.cancelAll()
                        });
                    }, 200)
                }
            },
            productTypes() {
                let steps = ['productTypes', 'details', 'connections']

                // Components
                if (
                    this.productTypes.indexOf('arrangement') !== -1 ||
                    this.productTypes.indexOf('flash_sale') !== -1 ||
                    this.productTypes.indexOf('package') !== -1 ||
                    this.productTypes.indexOf('hotel_only') !== -1
                ) {
                    steps = steps.concat(['components', 'notification', 'billing'])
                }

                // Flash sale
                if (this.productTypes.indexOf('flash_sale') !== -1) {
                    steps.push('flashSale')
                }

                // Voucher request
                if (this.productTypes.indexOf('voucher') !== -1) {
                    steps.push('voucherRequest')
                }

                // Summary
                steps.push('summary')

                this.steps = steps
            },
        },
        updated() {
            if ($) {
                $('[data-toggle="tooltip"]').tooltip();
            }
        }
    }
</script>
